(function (root) {
  'use strict';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const round = (value, digits = 0) => {
    const factor = 10 ** digits;
    return Math.round(value * factor) / factor;
  };
  const roundToFive = (value) => Math.round(value / 5) * 5;
  const allowed = (value, values, fallback) => values.includes(value) ? value : fallback;

  const dogExercise = (input) => {
    const profile = input || {};
    const age = clamp(Number(profile.age) || 0, 0.3, 22);
    const currentMinutes = clamp(Number(profile.currentMinutes) || 0, 0, 240);
    const energy = allowed(profile.breedGroup, ['low', 'medium', 'high', 'joint'], 'medium');
    const condition = allowed(profile.condition, ['lean', 'ideal', 'heavy'], 'ideal');
    const comfort = allowed(profile.comfort, ['clear', 'sensitive', 'recovering'], 'clear');
    const routine = allowed(profile.routine, ['walks', 'yard', 'training'], 'walks');
    const weather = allowed(profile.weather, ['mild', 'warm', 'hot', 'cold'], 'mild');
    const name = String(profile.name || '').trim() || 'Your dog';
    const energyLabels = {
      low: 'low-energy or short-nosed profile',
      medium: 'moderate-energy profile',
      high: 'high-energy or working profile',
      joint: 'giant or joint-sensitive profile'
    };
    const weatherLabels = {
      mild: 'mild weather',
      warm: 'warm or humid weather',
      hot: 'hot-weather swaps',
      cold: 'cold-weather swaps'
    };

    if (comfort === 'recovering') {
      return {
        primary: 'Vet plan',
        unit: 'first',
        score: 12,
        context: `${name} is marked as recovering or already following veterinary guidance.`,
        summary: 'Use the prescribed activity plan first. This tool can still help organize short sessions, indoor enrichment, and recovery observations.',
        basis: 'Recovery restrictions are individual, so this planner does not create a minute target for this profile.',
        plan: [
          { icon: 'medical services', title: 'Follow the restriction', text: 'Use the duration, surface, pace, and movement type already cleared by your veterinarian or rehabilitation professional.' },
          { icon: 'schedule', title: 'Keep sessions predictable', text: 'Split approved activity into calm blocks and avoid adding distance, speed, stairs, or jumping without clearance.' },
          { icon: 'visibility', title: 'Record recovery', text: 'Note willingness to move, limping, breathing, sleep, appetite, and next-day stiffness so changes are easier to discuss.' }
        ],
        hero: ['Vet plan', 'Short blocks', 'Track comfort'],
        note: 'Pain, collapse, breathing difficulty, sudden weakness, or refusal to move needs prompt veterinary guidance.'
      };
    }

    const adultBase = { low: 35, medium: 60, high: 90, joint: 45 };
    const puppyBase = { low: 25, medium: 35, high: 45, joint: 25 };
    let target = age < 1 ? puppyBase[energy] : adultBase[energy];
    if (age >= 8) target *= 0.75;
    if (condition === 'heavy') target *= 0.9;
    if (comfort === 'sensitive') target *= 0.78;
    target = clamp(roundToFive(target), 15, 105);

    const low = clamp(roundToFive(target * 0.85), 15, 105);
    const high = clamp(Math.max(low + 5, roundToFive(target * 1.15)), 20, 120);
    const sessions = age < 1 || age >= 8 || energy === 'joint' || comfort === 'sensitive' ? 4 : 3;
    const sessionLow = Math.max(4, Math.floor(low / sessions));
    const sessionHigh = Math.max(sessionLow + 1, Math.ceil(high / sessions));

    let comparison = `The current ${currentMinutes}-minute routine sits inside this planning range. Keep the pattern steady while recovery stays comfortable.`;
    if (currentMinutes === 0) comparison = 'No current routine was entered. Start with one or two short, easy blocks and build only after comfortable same-day and next-day recovery.';
    if (currentMinutes > 0 && currentMinutes < low) comparison = `The current ${currentMinutes}-minute routine is below this planning range. Add one short, easy block at a time instead of jumping straight to the upper end.`;
    if (currentMinutes > high) comparison = `The current ${currentMinutes}-minute routine is above this planning range. Keep that volume only if your dog chooses it willingly and shows no soreness, unusual fatigue, or next-day slowdown.`;

    const routineText = {
      walks: 'Use easy walking, sniff time, and a small amount of purposeful movement. Let pace and route variety do more work than speed.',
      yard: 'Count active play and purposeful movement, not quiet yard time. Add a sniff walk or simple training block for variety.',
      training: 'Balance sport or training days with lower-intensity sniffing, mobility-friendly walking, and a lighter recovery day.'
    };
    const weatherText = {
      mild: 'Use normal weather checks and carry water for longer outings.',
      warm: 'Move active blocks to cooler hours, choose shade, carry water, and shorten a session if panting or recovery changes.',
      hot: 'Replace outdoor intensity with brief relief walks and indoor movement. A fixed outdoor minute target is not appropriate in dangerous heat.',
      cold: 'Use shorter outdoor blocks when footing or temperature is uncomfortable, then finish with indoor movement or scent work.'
    };
    const bodyText = condition === 'heavy'
      ? 'Favor low-impact movement and use body-condition change as a veterinarian-guided goal, not a reason to force a longer walk.'
      : energy === 'joint'
        ? 'Favor even surfaces, controlled turns, and lower-impact movement; avoid using fatigue as the goal.'
        : 'Mix easy aerobic movement with sniffing and simple training while keeping the dog able to recover comfortably.';

    return {
      primary: `${low}-${high}`,
      unit: 'min/day',
      score: clamp(Math.round((target / 120) * 100), 18, 88),
      context: `${name} has a ${energyLabels[energy]}, age ${age.toFixed(age < 1 ? 1 : 0)}, with ${currentMinutes} current active minutes per day.`,
      summary: `Use ${low} to ${high} active minutes per day as a planning range, split into ${sessions} voluntary blocks. Comfortable recovery matters more than reaching the upper number.`,
      basis: 'The range combines life stage, natural energy and body type, body condition, care limits, and the current routine. Weight alone does not determine exercise needs.',
      plan: [
        { icon: 'trending up', title: 'Starting point', text: comparison },
        { icon: 'schedule', title: 'Session rhythm', text: `Plan about ${sessions} blocks of roughly ${sessionLow} to ${sessionHigh} minutes. Puppies, seniors, and joint-sensitive dogs usually benefit from shorter, more frequent sessions.` },
        { icon: 'directions walk', title: 'Movement mix', text: `${routineText[routine]} ${bodyText}` },
        { icon: 'partly cloudy day', title: 'Weather swap', text: weatherText[weather] },
        { icon: 'visibility', title: 'Recovery check', text: 'Shorten or stop for limping, heavy panting, heat stress, reluctance, unusual fatigue, pain signs, or a clear next-day slowdown.' }
      ],
      hero: [`${low}-${high}`, `${sessions} blocks`, weatherLabels[weather]],
      note: 'This is an educational planning range, not an exercise prescription. Health conditions, growth, pain, recovery, weather, surface, and individual response can change what is appropriate.'
    };
  };

  const catExercise = (input) => {
    const profile = input || {};
    const age = clamp(Number(profile.age) || 0, 0.3, 24);
    const currentMinutes = clamp(Number(profile.currentMinutes) || 0, 0, 180);
    const energy = allowed(profile.breedGroup, ['low', 'medium', 'high'], 'medium');
    const comfort = allowed(profile.comfort, ['clear', 'sensitive', 'recovering'], 'clear');
    const routine = allowed(profile.routine, ['solo', 'multi', 'shared'], 'solo');
    const resources = allowed(profile.resources, ['limited', 'some', 'varied'], 'some');
    const playStyle = allowed(profile.playStyle, ['wand', 'ground', 'forage', 'mixed'], 'mixed');
    const requestedWindow = Number(profile.sessionMinutes);
    const sessionMinutes = [5, 10, 15].includes(requestedWindow) ? requestedWindow : 10;
    const name = String(profile.name || '').trim() || 'Your cat';

    const energyLabels = {
      low: 'calm everyday play pattern',
      medium: 'steady everyday play pattern',
      high: 'high everyday play drive'
    };
    const playLabels = {
      wand: 'wand and chase play',
      ground: 'ground-level stalk and pounce play',
      forage: 'foraging and puzzle play',
      mixed: 'a mixed play rotation'
    };

    if (comfort === 'recovering') {
      return {
        primary: 'Vet plan',
        unit: 'first',
        score: 12,
        context: `${name} is marked as recovering or already following veterinary guidance.`,
        summary: 'Follow the activity limits already provided for your cat. Use this planner only to organize cleared, voluntary interaction and record changes.',
        basis: 'Recovery restrictions are individual, so this planner does not create an active-play target for this profile.',
        plan: [
          { icon: 'medical services', title: 'Follow the restriction', text: 'Use only the movement, surfaces, toy types, and session length already cleared by your veterinarian or rehabilitation professional.' },
          { icon: 'front hand', title: 'Keep play voluntary', text: 'Offer the cleared activity and let your cat choose whether to approach, pause, or stop. Do not chase, restrain, or force a position.' },
          { icon: 'home', title: 'Reduce effort at home', text: 'Keep food, water, litter, rest, and approved enrichment easy to reach without unnecessary jumping or slippery routes.' },
          { icon: 'visibility', title: 'Record changes', text: 'Note appetite, grooming, litter-box use, hiding, breathing, willingness to move, and any change from the usual routine.' }
        ],
        hero: ['Vet plan', 'Voluntary only', 'Track changes'],
        note: 'Open-mouth breathing, collapse, sudden weakness, severe pain, repeated vomiting, inability to urinate, or a rapid behavior change needs prompt veterinary guidance.'
      };
    }

    let target = age < 1 ? 35 : age >= 11 ? 18 : 24;
    if (energy === 'low') target -= 5;
    if (energy === 'high') target += 10;
    if (comfort === 'sensitive') target *= 0.75;
    target = clamp(roundToFive(target), 10, 45);

    const low = clamp(roundToFive(target * 0.85), 10, 45);
    const high = clamp(Math.max(low + 5, roundToFive(target * 1.2)), 15, 55);
    const sessionCountLow = clamp(Math.ceil(low / sessionMinutes), 1, 12);
    const sessionCountHigh = clamp(Math.ceil(high / sessionMinutes), sessionCountLow, 12);
    const sessionLabel = sessionCountLow === sessionCountHigh
      ? `${sessionCountLow} ${sessionCountLow === 1 ? 'session' : 'sessions'}`
      : `${sessionCountLow}-${sessionCountHigh} sessions`;

    let comparison = `The current ${currentMinutes}-minute play routine sits inside this planning range. Keep the pattern steady while your cat remains eager and recovers normally.`;
    if (currentMinutes === 0) comparison = 'No current play routine was entered. Begin with one short invitation at a predictable time and build only when your cat returns willingly.';
    if (currentMinutes > 0 && currentMinutes < low) comparison = `The current ${currentMinutes}-minute routine is below this planning range. Add one short invitation at a time instead of trying to make up the difference in one session.`;
    if (currentMinutes > high) comparison = `The current ${currentMinutes}-minute routine is above this planning range. Keep that amount only when your cat chooses it willingly and shows normal breathing, movement, appetite, and recovery.`;

    const playText = {
      wand: 'Move a supervised wand toy like prey: hide it, pause, let it escape, and allow a catch. Put string and wand toys away after the session.',
      ground: 'Use safe ground-level toys that dart behind cover, pause, and reappear. Avoid rapid turns or high jumps when comfort is limited.',
      forage: 'Hide part of a measured meal in easy locations or use a familiar puzzle feeder. Start simple enough that your cat can succeed without frustration.',
      mixed: 'Rotate supervised chase, stalk-and-pounce, foraging, climbing, and simple target play according to the choices your cat repeats.'
    };
    const resourceText = {
      limited: 'Start with one clear floor route, a stable scratcher, a hiding place, and a window or doorway observation spot. Add height only when access and landing are safe.',
      some: 'Use the floor plus one stable vertical or window route, a scratcher, and an easy foraging station. Keep escape routes open.',
      varied: 'Rotate floor, vertical, window, scratching, and foraging zones instead of presenting every option at once.'
    };
    const householdText = {
      solo: 'For a solo cat, place the session where your cat already chooses to watch, stalk, or rest nearby.',
      multi: 'For multiple cats with separate resources, offer individual play turns so a quieter cat is not crowded out.',
      shared: 'When cats share limited space, create distance between play, food, water, litter, scratching, and rest areas before adding intensity.'
    };

    return {
      primary: `${low}-${high}`,
      unit: 'min/day',
      score: clamp(Math.round((target / 55) * 100), 18, 88),
      context: `${name} has a ${energyLabels[energy]}, age ${age.toFixed(age < 1 ? 1 : 0)}, with ${currentMinutes} current active-play minutes per day.`,
      summary: `Use ${low} to ${high} voluntary active-play minutes per day as a planning range, split into about ${sessionLabel}. Your cat can end any session early.`,
      basis: 'The range combines life stage, everyday play energy, comfort, and the current routine. Play preference, household setup, home resources, and available session length shape how the plan is delivered.',
      plan: [
        { icon: 'trending up', title: 'Starting point', text: comparison },
        { icon: 'schedule', title: 'Session rhythm', text: `Plan about ${sessionLabel}, with each invitation lasting up to ${sessionMinutes} minutes. Start at the lower end when the upper end would create more sessions than your day or your cat's interest can support.` },
        { icon: 'toys', title: 'Preferred play flow', text: `${playText[playStyle]} When appropriate, finish with a small portion of the planned meal and a quiet settling period.` },
        { icon: 'home', title: 'Home setup', text: `${householdText[routine]} ${resourceText[resources]}` },
        { icon: 'calendar month', title: 'Seven-day rotation', text: 'Mon: preferred play. Tue: easy foraging. Wed: a floor or vertical route. Thu: the familiar toy with a new path. Fri: simple target or recall play. Sat: a choice-led mix. Sun: a gentle reset and toy safety check.' },
        { icon: 'recycling', title: 'Low-cost alternatives', text: 'Try a clean cardboard box, a paper packing sheet, a treat search, or furniture arranged as safe cover. Remove tape, staples, loose string, handles, and anything small enough to swallow.' },
        { icon: 'visibility', title: 'Comfort check', text: 'Pause for hiding, limping, repeated missed landings, open-mouth breathing, coughing, sudden irritability, or a clear drop in normal movement. A lasting change deserves veterinary advice.' }
      ],
      hero: [`${low}-${high}`, sessionLabel, playLabels[playStyle]],
      note: 'This is an educational play-planning range, not a medical exercise prescription. Sudden inactivity, pain, breathing changes, rapid weight change, or a major behavior change needs veterinary attention.'
    };
  };

  const dogFood = (input) => {
    const profile = input || {};
    const name = String(profile.name || '').trim() || 'Your dog';
    const careStatus = allowed(profile.careStatus, ['standard', 'medical', 'pregnant'], 'standard');
    const lifeStage = allowed(profile.lifeStage, ['puppy-young', 'puppy-older', 'adult', 'senior'], 'adult');
    const goal = allowed(profile.goal, ['maintain', 'lose', 'gain'], 'maintain');

    const vetPlan = (reason) => ({
      primary: 'Vet plan',
      unit: 'first',
      score: 12,
      context: `${name} has a feeding situation that needs an individualized calorie target.`,
      summary: 'Use the calorie and portion plan supplied by your veterinarian or veterinary nutrition professional before using a general food estimate.',
      basis: reason,
      plan: [
        { icon: 'medical services', title: 'Use the prescribed target', text: 'Follow the daily calorie target, diet choice, feeding route, and monitoring schedule already provided for your dog.' },
        { icon: 'restaurant', title: 'Measure the exact food', text: 'Use the kcal statement on the exact bag, can, pouch, or prescription label. Record the amount offered and the amount left.' },
        { icon: 'cookie', title: 'Count every extra', text: 'Include treats, chews, toppers, table food, training rewards, and anything used to give medication in the daily plan.' },
        { icon: 'monitor weight', title: 'Track the response', text: 'Record weight, body condition, appetite, stool, vomiting, thirst, energy, and any change your care team asked you to watch.' }
      ],
      hero: ['Vet plan', 'Exact food', 'Track response'],
      note: 'Pregnancy, nursing, growth concerns, prescription diets, major weight change, diabetes, kidney or heart disease, pancreatitis, digestive disease, and recovery need individualized feeding guidance.',
      details: { status: 'vet-plan' }
    });

    if (careStatus !== 'standard') {
      return vetPlan(careStatus === 'pregnant'
        ? 'Pregnancy and nursing can change energy needs quickly, so a general maintenance factor is not appropriate.'
        : 'A medical condition, prescription diet, or existing veterinary plan takes priority over a general maintenance estimate.');
    }

    if ((lifeStage === 'puppy-young' || lifeStage === 'puppy-older') && goal !== 'maintain') {
      return vetPlan('A growing puppy marked for weight loss or weight gain needs an individualized growth and body-condition plan.');
    }

    const unit = allowed(profile.unit, ['lb', 'kg'], 'lb');
    const currentWeight = Number(profile.currentWeight ?? profile.weight);
    const targetWeight = Number(profile.targetWeight);
    const toKg = (value) => unit === 'lb' ? value / 2.20462 : value;
    const currentKg = Number.isFinite(currentWeight) && currentWeight > 0 ? toKg(currentWeight) : 0;
    const targetKg = Number.isFinite(targetWeight) && targetWeight > 0 ? toKg(targetWeight) : 0;
    const weightBasis = allowed(profile.weightBasis, ['current', 'target'], 'current');
    if (!currentKg || (weightBasis === 'target' && !targetKg)) return null;
    const calculationKg = weightBasis === 'target' ? targetKg : currentKg;

    const neuterStatus = allowed(profile.neuterStatus, ['neutered', 'intact'], 'neutered');
    const activity = allowed(profile.activity, ['low', 'medium', 'high'], 'medium');
    const condition = allowed(profile.condition, ['lean', 'ideal', 'heavy'], 'ideal');
    const meals = clamp(Math.round(Number(profile.meals) || 2), 1, 4);
    const treatPercent = clamp(Math.round(Number(profile.treatPercent) || 0), 0, 10);
    const foodUnit = allowed(profile.foodUnit, ['cup', 'gram', 'can'], 'cup');
    const foodCalories = Number(profile.foodCalories ?? profile.kcalCup);
    if (!Number.isFinite(foodCalories) || foodCalories <= 0) return null;

    const factorReasons = [];
    let maintenanceFactor;
    if (lifeStage === 'puppy-young') {
      maintenanceFactor = 3;
      factorReasons.push('a puppy under about four months');
    } else if (lifeStage === 'puppy-older') {
      maintenanceFactor = 2;
      factorReasons.push('a growing puppy over about four months');
    } else {
      maintenanceFactor = neuterStatus === 'intact' ? 1.8 : 1.6;
      factorReasons.push(neuterStatus === 'intact' ? 'an intact adult starting factor' : 'a neutered adult starting factor');
      if (lifeStage === 'senior') {
        maintenanceFactor -= 0.2;
        factorReasons.push('a lower senior starting point');
      }
      if (activity === 'low') {
        maintenanceFactor -= 0.2;
        factorReasons.push('lower everyday activity');
      } else if (activity === 'high') {
        maintenanceFactor += 0.4;
        factorReasons.push('higher everyday activity');
      }
      if (goal === 'lose') {
        maintenanceFactor = 1;
        factorReasons.push('a conservative weight-loss starting factor');
      } else if (goal === 'gain') {
        maintenanceFactor += 0.2;
        factorReasons.push('a gradual weight-gain starting point');
      } else if (condition === 'heavy') {
        maintenanceFactor -= 0.2;
        factorReasons.push('a heavy body-condition adjustment');
      } else if (condition === 'lean') {
        maintenanceFactor += 0.2;
        factorReasons.push('a lean body-condition adjustment');
      }
    }
    maintenanceFactor = clamp(round(maintenanceFactor, 1), 1, 3);

    const rer = Math.round(70 * Math.pow(calculationKg, 0.75));
    const calories = Math.round(rer * maintenanceFactor);
    const treatCalories = Math.round(calories * (treatPercent / 100));
    const mainFoodCalories = calories - treatCalories;
    const amountPerDay = foodUnit === 'gram'
      ? (mainFoodCalories / foodCalories) * 100
      : mainFoodCalories / foodCalories;
    const amountPerMeal = amountPerDay / meals;

    const formatAmount = (value) => {
      if (foodUnit === 'gram') return `${Math.round(value)} g`;
      const rounded = round(value, 2);
      if (foodUnit === 'cup') return `${rounded} ${Math.abs(rounded - 1) < 0.01 ? 'cup' : 'cups'}`;
      return `${rounded} ${Math.abs(rounded - 1) < 0.01 ? 'can/pouch' : 'cans/pouches'}`;
    };
    const densityText = foodUnit === 'gram'
      ? `${foodCalories} kcal per 100 g`
      : foodUnit === 'cup'
        ? `${foodCalories} kcal per cup`
        : `${foodCalories} kcal per can or pouch`;
    const measuringTip = foodUnit === 'gram'
      ? 'Use a kitchen scale for a repeatable gram amount.'
      : foodUnit === 'cup'
        ? 'Use the same level measuring cup each time, or weigh the portion when the label also gives gram guidance.'
        : 'Measure partial containers consistently and store opened food as the label directs.';
    const lifeStageText = {
      'puppy-young': 'young puppy',
      'puppy-older': 'older puppy',
      adult: 'adult',
      senior: 'senior'
    }[lifeStage];
    const selectedWeightText = weightBasis === 'target' && targetKg > 0
      ? `the entered goal or ideal weight of ${round(calculationKg, 1)} kg`
      : `the current weight of ${round(calculationKg, 1)} kg`;

    return {
      primary: String(calories),
      unit: 'kcal/day',
      score: clamp(Math.round((maintenanceFactor / 3) * 100), 18, 92),
      context: `${name} is entered as a ${lifeStageText} at ${round(currentKg, 1)} kg, with the calculation based on ${selectedWeightText}.`,
      summary: `Start near ${calories} kcal per day. After reserving ${treatCalories} kcal (${treatPercent}%) for treats and extras, measure about ${formatAmount(amountPerDay)} of the main food across ${meals} meal${meals === 1 ? '' : 's'}.`,
      basis: `RER = 70 × ${round(calculationKg, 1)} kg^0.75 = ${rer} kcal. The selected ${maintenanceFactor.toFixed(1)} × RER factor reflects ${factorReasons.join(', ')}. This is a starting estimate, not a fixed prescription.`,
      plan: [
        { icon: 'calculate', title: 'Calorie method', text: `${rer} kcal RER × ${maintenanceFactor.toFixed(1)} maintenance factor = ${calories} kcal per day.` },
        { icon: 'restaurant', title: 'Main-food portion', text: `${mainFoodCalories} kcal for the main food equals about ${formatAmount(amountPerDay)} per day when the label shows ${densityText}.` },
        { icon: 'cookie', title: 'Treat allowance', text: `${treatCalories} kcal remain for treats, chews, toppers, table food, training rewards, and medication extras. Keep the total within this allowance.` },
        { icon: 'schedule', title: 'Per meal', text: `Split the main food into ${meals} meal${meals === 1 ? '' : 's'} of about ${formatAmount(amountPerMeal)} each. ${measuringTip}` },
        { icon: 'monitor weight', title: 'Review after 10 to 14 days', text: 'After 10 to 14 days, check the weight and body-condition trend along with appetite, stool, energy, and hunger. If the trend is not appropriate, make a small measured change and review it with your veterinarian.' },
        { icon: 'fact check', title: 'Compare with the exact label', text: 'Confirm the calorie statement for the exact recipe and package. Feeding charts are useful comparisons, but the measured trend in your individual dog decides whether the starting amount needs adjustment.' }
      ],
      hero: [String(calories), `${maintenanceFactor.toFixed(1)} × RER`, formatAmount(amountPerDay)],
      note: 'This educational estimate is for generally healthy dogs. Rapid weight change, poor appetite, vomiting, diarrhea, unusual thirst, pregnancy, nursing, growth concerns, prescription diets, or a medical condition needs veterinary guidance.',
      details: {
        amountPerDay,
        amountPerMeal,
        calculationKg,
        calories,
        foodCalories,
        foodUnit,
        mainFoodCalories,
        maintenanceFactor,
        rer,
        treatCalories,
        treatPercent
      }
    };
  };

  const sizeFit = (input) => {
    const profile = input || {};
    const mode = allowed(profile.mode, ['harness', 'bed', 'travel', 'mobility'], 'harness');
    const unit = allowed(profile.unit, ['in', 'cm'], 'in');
    const species = allowed(profile.species, ['dog', 'cat'], 'dog');
    const name = String(profile.name || '').trim() || (species === 'cat' ? 'Your cat' : 'Your dog');
    const toInches = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return 0;
      return unit === 'cm' ? numeric / 2.54 : numeric;
    };
    const display = (inches, roundUp = false) => {
      if (unit === 'cm') {
        const value = roundUp ? Math.ceil(inches * 2.54) : round(inches * 2.54, 1);
        return `${value} cm`;
      }
      const value = roundUp ? Math.ceil(inches * 2) / 2 : round(inches, 1);
      return `${value} in`;
    };
    const dimensionValue = (inches, roundUp = false) => {
      if (unit === 'cm') return roundUp ? Math.ceil(inches * 2.54) : round(inches * 2.54, 1);
      return roundUp ? Math.ceil(inches * 2) / 2 : round(inches, 1);
    };
    const dimensions = (values, roundUp = false) => `${values.map((value) => dimensionValue(value, roundUp)).join(' × ')} ${unit}`;

    if (mode === 'harness') {
      const neck = toInches(profile.neck);
      const chest = toInches(profile.chest);
      const back = toInches(profile.backLength);
      if (!neck || !chest || !back) return null;
      return {
        primary: `${display(chest)} chest`,
        unit: 'measurement',
        title: 'Harness measurements ready',
        copy: `${name}'s chest, lower-neck, and back measurements are ready to compare with the exact harness size chart. This tool does not assign a universal S, M, or L.`,
        basis: 'Harness brands use different pattern shapes and adjustment ranges, so the product chart and a physical fit check decide the size.',
        plan: [
          { icon: 'straighten', title: 'Chest girth', text: `${display(chest)} around the widest part of the rib cage, measured while standing without pulling the tape tight.` },
          { icon: 'straighten', title: 'Lower neck', text: `${display(neck)} where the harness neckline will sit, not high at the collar position.` },
          { icon: 'straighten', title: 'Back reference', text: `${display(back)} from the shoulder-blade area to the tail base. Use it only when the product chart asks for body or back length.` },
          { icon: 'fact check', title: 'Compare the exact chart', text: 'Match every required measurement to the same product chart. When measurements fall into different rows, contact the seller or choose a different harness pattern instead of guessing from weight.' },
          { icon: 'health and safety', title: 'Check the fitted harness', text: 'Confirm comfortable finger space, clear shoulder movement, no rubbing behind the front legs, no twisting, and no route for your pet to back out.' }
        ],
        note: 'Recheck fit after coat changes, growth, weight change, or visible rubbing. A harness should not press on the throat or restrict normal movement.',
        details: { back, chest, mode, neck, unit }
      };
    }

    if (mode === 'bed') {
      const sleepingLength = toInches(profile.sleepingLength);
      const sleepingWidth = toInches(profile.sleepingWidth);
      const sleepStyle = allowed(profile.sleepStyle, ['curled', 'mixed', 'stretched'], 'mixed');
      if (!sleepingLength || !sleepingWidth) return null;
      const lengthFactor = sleepStyle === 'curled' ? 1.15 : 1.25;
      const widthFactor = sleepStyle === 'stretched' ? 1.15 : 1.2;
      const interiorLength = sleepingLength * lengthFactor;
      const interiorWidth = sleepingWidth * widthFactor;
      const styleText = sleepStyle === 'curled' ? 'mostly curled' : sleepStyle === 'stretched' ? 'mostly stretched out' : 'a mix of curled and stretched positions';
      return {
        primary: dimensions([interiorLength, interiorWidth], true),
        unit: 'usable interior',
        title: `Look for at least ${dimensions([interiorLength, interiorWidth], true)} inside`,
        copy: `${name} sleeps in ${styleText}. Use the clear interior sleep surface, not the product's outside edge, when comparing beds or resting mats.`,
        basis: `The starting interior adds settling room to the entered sleeping footprint: ${sleepStyle === 'curled' ? '15%' : '25%'} in length and ${Math.round((widthFactor - 1) * 100)}% in width.`,
        plan: [
          { icon: 'straighten', title: 'Measured sleeping footprint', text: `${dimensions([sleepingLength, sleepingWidth])} while resting naturally.` },
          { icon: 'bed', title: 'Minimum usable interior', text: `${dimensions([interiorLength, interiorWidth], true)} of clear sleep space before bolsters, raised edges, or thick side walls take room away.` },
          { icon: 'open in full', title: 'Check the entry and edges', text: 'Make sure the entry height, bolster shape, and cushion compression suit the way your pet steps in, turns, stretches, and gets up.' },
          { icon: 'home', title: 'Confirm the home footprint', text: 'Compare the bed exterior with the room, crate, or furniture space where it will sit. Interior comfort and exterior footprint are different measurements.' }
        ],
        note: 'This is a comfort starting point. Shared beds, thick bolsters, limited mobility, and pets who change sleep positions may need more usable room.',
        details: { interiorLength, interiorWidth, mode, sleepingLength, sleepingWidth, sleepStyle, unit }
      };
    }

    if (mode === 'travel') {
      const travelType = allowed(profile.travelType, ['cargo', 'cabin'], 'cargo');
      const a = toInches(profile.travelA);
      const b = toInches(profile.travelB);
      const c = toInches(profile.travelC);
      const d = toInches(profile.travelD);
      if (!a || !b || !c || !d) return null;
      if (travelType === 'cabin') {
        return {
          primary: 'Airline check',
          unit: 'required',
          title: 'Confirm the exact airline and aircraft first',
          copy: `${name}'s four travel measurements are ready, but cabin carrier limits depend on the airline, route, fare, aircraft, seat, and current pet policy.`,
          basis: 'Cabin acceptance is controlled by the operating airline. An animal measurement or a retailer carrier label cannot prove that a carrier will be accepted on a specific flight.',
          plan: [
            { icon: 'straighten', title: 'Pet measurement set', text: `A ${display(a)}, B ${display(b)}, C ${display(c)}, and D ${display(d)}. Keep these values with your booking notes.` },
            { icon: 'flight', title: 'Ask the operating airline', text: 'Confirm the maximum carrier exterior length, width, and height for the exact aircraft and seat, the pet reservation limit, weight rules, documentation, fees, and check-in process.' },
            { icon: 'pets', title: 'Check movement inside', text: 'Your pet must be able to remain comfortable and meet the airline requirement for standing, turning, and lying down inside the accepted carrier.' },
            { icon: 'fact check', title: 'Reconfirm before travel', text: 'Aircraft and policies can change. Recheck the booking and carrier measurements shortly before departure and again if the itinerary changes.' }
          ],
          note: 'This result does not confirm airline approval. Contact the operating airline before buying a cabin carrier or traveling.',
          details: { a, b, c, d, mode, travelType, unit }
        };
      }
      const crateLength = a + (b / 2);
      const crateWidth = c * 2;
      const crateHeight = d;
      return {
        primary: dimensions([crateLength, crateWidth, crateHeight], true),
        unit: 'minimum interior',
        title: `Cargo crate starting interior: ${dimensions([crateLength, crateWidth, crateHeight], true)}`,
        copy: `The IATA measurement method uses A + half of B for length, twice C for width, and D for height. Compare the rounded-up result with current airline and crate requirements.`,
        basis: `Entered measurements: A ${display(a)}, B ${display(b)}, C ${display(c)}, D ${display(d)}. Formula result before rounding: ${dimensions([crateLength, crateWidth, crateHeight])}.`,
        plan: [
          { icon: 'calculate', title: 'IATA length', text: `A + ½B = ${display(a)} + half of ${display(b)} = ${display(crateLength, true)} minimum interior length.` },
          { icon: 'calculate', title: 'IATA width and height', text: `2C = ${display(crateWidth, true)} minimum interior width. D = ${display(crateHeight, true)} minimum interior height.` },
          { icon: 'inventory 2', title: 'Check the actual crate', text: 'Verify usable interior space, ventilation, fasteners, door, floor, handles, wheels, labels, bowls, and any airline-specific hardware requirement.' },
          { icon: 'flight', title: 'Confirm current acceptance', text: 'Ask the operating airline about the route, aircraft, breed restrictions, documentation, seasonal limits, and its current interpretation of IATA container rules.' }
        ],
        note: 'This is a measurement calculation, not a guarantee of airline acceptance. Current IATA and operating-airline rules take priority.',
        details: { a, b, c, crateHeight, crateLength, crateWidth, d, mode, travelType, unit }
      };
    }

    const accessHeight = toInches(profile.accessHeight);
    const availableRun = toInches(profile.availableRun);
    const comfort = allowed(profile.mobilityComfort, ['clear', 'cautious', 'guided'], 'clear');
    if (!accessHeight || !availableRun) return null;
    const actualAngle = round(Math.atan(accessHeight / availableRun) * (180 / Math.PI), 1);
    const runFor = (angle) => accessHeight / Math.tan(angle * Math.PI / 180);
    const gentleRun = runFor(18);
    const moderateRun = runFor(22);
    const compactRun = runFor(25);
    const comfortText = comfort === 'guided'
      ? 'Follow the incline, step, and movement limits already provided by your veterinarian or rehabilitation professional.'
      : comfort === 'cautious'
        ? 'Because your pet is cautious, prioritize the gentler feasible option, stable footing, and supervised practice.'
        : 'Start with stable footing and supervised practice, then watch how willingly and comfortably your pet uses the access aid.';
    return {
      primary: `${actualAngle}°`,
      unit: 'available incline',
      title: `Your available run creates an incline of about ${actualAngle}°`,
      copy: `${display(accessHeight)} of rise over ${display(availableRun)} of floor length creates the entered access angle. Use the reference lengths below to compare ramps, stairs, and the space you have.`,
      basis: 'Incline is calculated from rise and horizontal run. The reference lengths are shopping comparisons, not a medical prescription for an individual pet.',
      plan: [
        { icon: 'straighten', title: 'Measured space', text: `${display(accessHeight)} rise and ${display(availableRun)} available horizontal run, creating about ${actualAngle}°.` },
        { icon: 'ramp right', title: 'Ramp-length references', text: `About ${display(gentleRun, true)} of run for 18°, ${display(moderateRun, true)} for 22°, or ${display(compactRun, true)} for 25°. Gentler angles require more floor space.` },
        { icon: 'health and safety', title: 'Match comfort and guidance', text: comfortText },
        { icon: 'fact check', title: 'Shopping checks', text: 'Compare usable width, grip, edge protection, weight rating, stability, step rise and tread depth, attachment method, storage, and the landing area at both ends.' },
        { icon: 'visibility', title: 'Watch real use', text: 'Stop if your pet slips, jumps off, freezes, shows pain, or avoids the aid. Do not force practice, and ask your veterinarian about new or worsening mobility changes.' }
      ],
      note: 'New weakness, dragging, collapse, severe pain, inability to stand, or a sudden mobility change needs prompt veterinary guidance.',
      details: { accessHeight, actualAngle, availableRun, compactRun, comfort, gentleRun, mode, moderateRun, unit }
    };
  };

  const seniorMobility = (input) => {
    const profile = input || {};
    const species = allowed(profile.species, ['dog', 'cat'], 'dog');
    const name = String(profile.name || '').trim() || (species === 'cat' ? 'Your cat' : 'Your dog');
    const rising = allowed(profile.rising, ['usual', 'slower', 'help'], 'usual');
    const lyingDown = allowed(profile.lyingDown, ['usual', 'careful', 'difficulty'], 'usual');
    const balance = allowed(profile.balance, ['steady', 'wobbly', 'fall'], 'steady');
    const walking = allowed(profile.walking, ['usual', 'slower', 'short'], 'usual');
    const stairs = allowed(profile.stairs, ['usual', 'hesitates', 'avoids'], 'usual');
    const slipping = allowed(profile.slipping, ['none', 'some', 'frequent'], 'none');
    const comfort = allowed(profile.comfort, ['comfortable', 'stiff', 'pain'], 'comfortable');
    const sleep = allowed(profile.sleep, ['usual', 'restless', 'more'], 'usual');
    const favorite = allowed(profile.favorite, ['same', 'reduced', 'stopped'], 'same');
    const urgent = allowed(profile.urgent, ['none', 'unable', 'collapse', 'severe'], 'none');
    const repeatDays = [7, 14].includes(Number(profile.repeatDays)) ? Number(profile.repeatDays) : 7;
    const checkDate = /^\d{4}-\d{2}-\d{2}$/.test(String(profile.checkDate || '')) ? String(profile.checkDate) : '';
    const repeatDate = (() => {
      if (!checkDate) return `in ${repeatDays} days`;
      const date = new Date(`${checkDate}T00:00:00Z`);
      if (Number.isNaN(date.getTime())) return `in ${repeatDays} days`;
      date.setUTCDate(date.getUTCDate() + repeatDays);
      return date.toISOString().slice(0, 10);
    })();

    const severity = {
      usual: 0,
      steady: 0,
      none: 0,
      comfortable: 0,
      same: 0,
      slower: 1,
      careful: 1,
      wobbly: 1,
      hesitates: 1,
      some: 1,
      stiff: 1,
      restless: 1,
      more: 1,
      reduced: 1,
      help: 2,
      difficulty: 2,
      fall: 2,
      short: 2,
      avoids: 2,
      frequent: 2,
      pain: 2,
      stopped: 2
    };
    const labels = {
      rising: { usual: 'rises as usual', slower: 'rises more slowly', help: 'needs help to rise' },
      lyingDown: { usual: 'lies down as usual', careful: 'lies down carefully', difficulty: 'has difficulty lying down' },
      balance: { steady: 'steady balance', wobbly: 'wobbling or missteps', fall: 'falls or gives way' },
      walking: { usual: 'usual walking pattern', slower: 'slower than usual', short: 'stops early or manages short walks only' },
      stairs: { usual: 'uses steps or jumps as usual', hesitates: 'hesitates or slows', avoids: 'avoids or needs help' },
      slipping: { none: 'no observed slipping', some: 'occasional slips', frequent: 'frequent slips' },
      comfort: { comfortable: 'no clear discomfort observed', stiff: 'stiff after rest or activity', pain: 'limping, pain signs, or guarding' },
      sleep: { usual: 'usual sleep', restless: 'restless or changes position often', more: 'sleeping or resting much more' },
      favorite: { same: 'favorite activities unchanged', reduced: 'less interest or shorter participation', stopped: 'stopped a favorite activity' }
    };
    const observations = [
      ['Rising', rising, labels.rising[rising]],
      ['Lying down', lyingDown, labels.lyingDown[lyingDown]],
      ['Balance', balance, labels.balance[balance]],
      ['Walking', walking, labels.walking[walking]],
      ['Steps and jumping', stairs, labels.stairs[stairs]],
      ['Slipping', slipping, labels.slipping[slipping]],
      ['Comfort', comfort, labels.comfort[comfort]],
      ['Sleep', sleep, labels.sleep[sleep]],
      ['Favorite activity', favorite, labels.favorite[favorite]]
    ];
    const changedCount = observations.filter(([, value]) => severity[value] > 0).length;
    const significantCount = observations.filter(([, value]) => severity[value] === 2).length;
    const total = observations.reduce((sum, [, value]) => sum + severity[value], 0);
    const directConcern = comfort === 'pain' || balance === 'fall' || rising === 'help' || favorite === 'stopped';

    let band = 'monitor';
    if (total >= 3 || changedCount >= 3) band = 'support';
    if (directConcern || total >= 8 || significantCount >= 2) band = 'contact';
    if (urgent !== 'none') band = 'urgent';

    const bandContent = {
      monitor: {
        primary: 'Monitor',
        title: 'Keep the routine steady and repeat the check',
        copy: 'Few changes were marked over the past seven days. Keep easy routes and watch for a new or lasting change.',
        next: 'Continue comfortable, voluntary activity and repeat the same observations on the planned date.'
      },
      support: {
        primary: 'Plan support',
        title: 'Add home support and arrange a routine review',
        copy: 'Several mobility changes were marked. Reduce avoidable strain, document the pattern, and discuss it at the next appropriate veterinary visit.',
        next: 'Add traction and easier access now, then book a non-urgent veterinary review if the change is new, lasting, or getting worse.'
      },
      contact: {
        primary: 'Contact veterinarian',
        title: 'Contact your veterinarian about these mobility changes',
        copy: 'One or more important changes were marked. Gear may help the home setup, but pain, falling, loss of function, or stopping favorite activities needs clinical guidance.',
        next: 'Contact your veterinarian promptly, describe when the change began, and ask what activity and home support are appropriate while you wait.'
      },
      urgent: {
        primary: 'Urgent care',
        title: 'Seek urgent veterinary guidance now',
        copy: 'A red-flag change was selected. Do not wait for the repeat-check date or rely on mobility products to manage it.',
        next: 'Call your veterinarian or an emergency clinic now and follow their transport and handling instructions.'
      }
    }[band];
    const urgentText = {
      none: 'No urgent red flag selected.',
      unable: 'Sudden weakness, dragging, or inability to stand or walk was selected.',
      collapse: 'Collapse or breathing difficulty was selected.',
      severe: 'Severe pain, major injury, or rapid loss of function was selected.'
    }[urgent];
    const summary = observations.map(([label, , text]) => `${label}: ${text}`).join('; ');
    const surfaceText = slipping === 'frequent' || slipping === 'some'
      ? 'Create continuous non-slip paths between rest, food, water, toileting, doors, and favorite places. Remove loose mats and block unsafe jumps.'
      : 'Keep the main route clear, well lit, and predictable. Add traction before slipping becomes part of the pattern.';
    const activityText = favorite === 'same'
      ? 'Protect the activities your pet still chooses by shortening the route or session before fatigue changes the experience.'
      : favorite === 'reduced'
        ? 'Offer a shorter, easier version of the favorite activity and let your pet stop. Note whether interest returns with rest or support.'
        : 'Do not push the stopped activity. Record what your pet avoids and discuss the change with your veterinarian.';
    const repeatWhen = checkDate ? `on ${repeatDate}` : repeatDate;
    const repeatPlan = band === 'urgent'
      ? {
          icon: 'emergency',
          title: 'Do not wait for the repeat check',
          text: 'Get urgent veterinary guidance now. Restart this checklist only after the urgent problem has been assessed and your care team tells you what to monitor.'
        }
      : {
          icon: 'event repeat',
          title: 'Repeat check',
          text: `Repeat the same checklist ${repeatWhen}. If a change worsens before then, use the more urgent next step instead of waiting.`
        };

    return {
      primary: bandContent.primary,
      unit: 'next step',
      title: bandContent.title,
      copy: bandContent.copy,
      basis: `This observation band uses the past seven days: ${changedCount} of 9 areas changed, including ${significantCount} marked as major difficulty. It does not diagnose arthritis or identify a cause.`,
      plan: [
        { icon: 'fact check', title: 'Seven-day observation summary', text: summary },
        { icon: band === 'urgent' ? 'emergency' : 'medical services', title: 'Next step', text: `${bandContent.next} ${urgentText}` },
        { icon: 'home', title: 'Make daily routes easier', text: surfaceText },
        { icon: 'favorite', title: 'Protect favorite activities', text: activityText },
        repeatPlan,
        { icon: 'video camera back', title: 'Prepare useful notes', text: 'Save this summary and, when safe, record short videos of rising, walking, turning, and using steps. Note when the change started, medications, appetite, toileting, and recent slips or injuries.' }
      ],
      note: 'Sudden weakness, dragging, inability to stand, collapse, breathing difficulty, severe pain, major injury, or rapid loss of function needs urgent veterinary guidance.',
      details: { band, changedCount, checkDate, observations, repeatDate, significantCount, total, urgent }
    };
  };

  const travelReadiness = (input) => {
    const profile = input || {};
    const pet = allowed(profile.pet, ['dog', 'cat'], 'dog');
    const mode = allowed(profile.mode, ['local', 'car', 'stay', 'flight'], 'car');
    const duration = allowed(profile.duration, ['under_hour', 'day', 'multi_day'], 'day');
    const destination = allowed(profile.destination, ['familiar', 'hotel', 'outdoor', 'cross_border'], 'familiar');
    const weather = allowed(profile.weather, ['mild', 'hot', 'cold', 'mixed'], 'mild');
    const carrier = allowed(profile.carrier, ['ready', 'some', 'none'], 'some');
    const documents = allowed(profile.documents, ['ready', 'partial', 'missing'], 'partial');
    const medication = allowed(profile.medication, ['none', 'daily', 'timed'], 'none');
    const stress = allowed(profile.stress, ['calm', 'uneasy', 'severe'], 'calm');
    const health = allowed(profile.health, ['stable', 'changed', 'restricted'], 'stable');
    const tripDate = /^\d{4}-\d{2}-\d{2}$/.test(String(profile.tripDate || '')) ? String(profile.tripDate) : '';
    const today = /^\d{4}-\d{2}-\d{2}$/.test(String(profile.today || '')) ? String(profile.today) : '';
    const modeLabel = {
      local: 'Local outing',
      car: 'Car trip',
      stay: 'Overnight stay',
      flight: 'Flight'
    }[mode];
    const tasks = [];
    const add = (id, phase, title, text, priority = 'standard') => {
      tasks.push({ id, phase, title, text, priority });
    };

    add(
      'destination-rules',
      'rules',
      'Confirm every destination rule',
      destination === 'hotel'
        ? 'Contact the property directly about pet limits, fees, room access, relief areas, unattended-pet rules, and any required records.'
        : destination === 'outdoor'
          ? 'Check the park, trail, campground, beach, or event rules, including leash, wildlife, seasonal, and pet-access limits.'
          : destination === 'cross_border'
            ? 'Check the official import, export, transit, identification, vaccination, and health-certificate rules for every country or territory on the route.'
            : 'Confirm pet access, emergency contacts, opening hours, and a backup destination before leaving.',
      destination === 'cross_border' ? 'critical' : 'standard'
    );
    add(
      'identity-records',
      'rules',
      'Verify identification and records',
      'Check the tag and microchip contact details, save a recent pet photo, and keep veterinary, vaccination, prescription, and emergency-contact information easy to reach.',
      documents === 'missing' ? 'critical' : 'standard'
    );
    add(
      'document-gap',
      'rules',
      documents === 'ready' ? 'Recheck document validity' : 'Finish the missing document checks',
      documents === 'ready'
        ? 'Confirm names, dates, signatures, microchip numbers, and route details still match the trip. Keep digital and paper copies where required.'
        : documents === 'partial'
          ? 'List each missing record or confirmation, who must provide it, and its deadline. Do not assume a vaccination record is the same as a health certificate.'
          : 'Pause booking commitments that depend on records until the airline, destination, lodging, and veterinary requirements are confirmed.',
      documents === 'ready' ? 'standard' : 'critical'
    );

    if (mode === 'flight') {
      add(
        'airline-confirmation',
        'rules',
        'Confirm the exact flight with the airline',
        'Ask the airline about species, route, aircraft, seat, carrier dimensions, weight, paperwork, fees, connection, temperature, and check-in rules before buying a carrier or relying on this plan.',
        'critical'
      );
      add(
        'flight-carrier-fit',
        'practice',
        'Measure the pet and the carrier',
        'Measure standing length, width at the widest point, and natural standing height. Compare those measurements with the carrier interior and the exact airline or cargo-container method. A product label cannot guarantee acceptance.',
        'critical'
      );
      add(
        'flight-day-route',
        'travel',
        'Map the full airport and connection route',
        'Plan check-in, security, relief, water, connections, delays, and pickup. Keep the carrier closed in unsecured areas and follow staff instructions for screening.',
        'standard'
      );
    } else if (mode === 'car') {
      add(
        'car-restraint',
        'practice',
        'Set up a secure car position',
        'Use a carrier or vehicle restraint as directed by its maker, place it away from an active front airbag, and test the setup before the trip.',
        'critical'
      );
      add(
        'car-breaks',
        'travel',
        'Plan calm, controlled breaks',
        duration === 'multi_day'
          ? 'Map overnight stops plus safe water, food, toileting, and rest breaks. Keep the leash or carrier secure before any door opens.'
          : 'Choose safe places for water, toileting, and a quiet reset. Keep the leash or carrier secure before any door opens.',
        'standard'
      );
      add(
        'parked-car',
        'travel',
        'Keep the pet with a responsible adult',
        'Do not leave a pet in a parked vehicle. Heat, cold, stress, theft, and equipment failure can turn a short stop into an emergency.',
        'critical'
      );
    } else if (mode === 'stay') {
      add(
        'room-safety',
        'practice',
        'Inspect the room before settling in',
        'Check doors, windows, balconies, hiding spaces, cords, cleaning products, plants, food, and escape routes before opening the carrier or unclipping a leash.',
        'critical'
      );
      add(
        'stay-routine',
        'travel',
        'Keep the first hours predictable',
        pet === 'cat'
          ? 'Set up litter, water, food, hiding space, and a familiar resting item before giving access to the room.'
          : 'Locate the relief route, offer water, and begin with a short calm walk before asking for rest in the new space.',
        'standard'
      );
    } else {
      add(
        'local-control',
        'practice',
        'Check the leash, harness, or carrier',
        pet === 'cat'
          ? 'Use a secure carrier for transport and check every closure before leaving. Practice the complete door-to-door route at home.'
          : 'Check fit, clips, leash condition, identification, and control at doors, parking areas, crowds, and other high-distraction points.',
        'critical'
      );
      add(
        'local-route',
        'travel',
        'Choose an easy exit and return plan',
        'Know where you can pause, turn back, find water, avoid crowds, and return quickly if the pet shows stress, fatigue, pain, or weather discomfort.',
        'standard'
      );
    }

    if (carrier === 'ready') {
      add('carrier-check', 'practice', 'Recheck carrier comfort', 'Run one calm practice with the travel bedding and normal closures. Confirm that the pet can settle and that every fastener still works.', 'standard');
    } else if (carrier === 'some') {
      add('carrier-practice', 'practice', 'Add short carrier or restraint practice', 'Use several calm sessions with the door open first, then brief closed and moving practice. Stop before fear builds and reward voluntary entry.', mode === 'flight' ? 'critical' : 'standard');
    } else {
      add('carrier-introduction', 'practice', 'Start carrier or restraint training now', 'Do not make travel day the first full session. Begin with voluntary exploration and short, positive steps. Ask a veterinarian or qualified behavior professional for help if fear is intense.', 'critical');
    }

    if (stress === 'uneasy') {
      add('stress-plan', 'practice', 'Build a low-stress rehearsal', 'Practice the exact sounds, movement, handling, and duration in smaller steps. Prepare a quiet waiting area and a backup plan if the pet cannot settle.', 'standard');
    } else if (stress === 'severe') {
      add('stress-vet', 'rules', 'Talk with the veterinarian about severe travel stress', 'Discuss whether the trip is appropriate and how to rehearse or manage it. Do not give sedatives, supplements, or human medicines without veterinary direction.', 'critical');
    }

    if (health === 'changed') {
      add('health-review', 'rules', 'Ask the veterinarian about the recent health change', 'Describe what changed, when it began, the planned route, and the travel conditions. Ask whether travel should wait and what signs mean stop or seek care.', 'critical');
    } else if (health === 'restricted') {
      add('health-clearance', 'rules', 'Get trip-specific veterinary guidance', 'Confirm permitted activity, medication timing, feeding, temperature limits, lifting, toileting, and emergency steps for this trip.', 'critical');
    }

    if (medication !== 'none') {
      add(
        'medication-plan',
        'packing',
        'Pack and schedule medication',
        medication === 'timed'
          ? 'Carry enough labeled medication plus the permitted backup supply, record time-zone changes, and keep every dose and feeding time in an accessible schedule.'
          : 'Carry enough labeled medication plus the permitted backup supply. Keep it accessible and follow storage, food, and timing instructions.',
        'critical'
      );
    }

    add(
      'food-water',
      'packing',
      'Pack measured food and accessible water',
      duration === 'multi_day'
        ? 'Pack the usual food in labeled portions, extra permitted supply, bowls, water, treats, and a plan for safe storage and replenishment.'
        : 'Pack the usual food or treats only as needed, plus water and a bowl that stay accessible during the trip.',
      'standard'
    );
    add(
      'toileting-cleanup',
      'packing',
      pet === 'cat' ? 'Pack litter and cleanup supplies' : 'Pack relief and cleanup supplies',
      pet === 'cat'
        ? 'Bring the familiar litter, tray or travel setup, liners, bags, wipes, and a private place to use it.'
        : 'Bring waste bags, wipes, an absorbent towel, and any pads needed for delays or restricted relief access.',
      'standard'
    );
    add('comfort-emergency', 'packing', 'Keep comfort and emergency items within reach', 'Pack a familiar resting item, recent photo, spare leash or closure, towel, basic cleanup supplies, emergency contacts, and route-appropriate first-aid items.', 'standard');

    if (weather !== 'mild') {
      add(
        'weather-plan',
        'travel',
        'Set weather limits and a backup plan',
        weather === 'hot'
          ? 'Travel during cooler periods, keep shade and water available, avoid hot surfaces, and change plans if safe temperature control is uncertain.'
          : weather === 'cold'
            ? 'Keep the pet dry, limit exposure, protect paws where appropriate, and provide a warm resting setup without unsafe direct heat.'
            : 'Pack for changing conditions and decide in advance which heat, cold, storm, smoke, or surface conditions will make you delay, shorten, or cancel.',
        'critical'
      );
    }

    const phaseLabels = {
      rules: 'Rules and health checks',
      practice: 'Fit and practice',
      packing: 'Pack within reach',
      travel: 'Travel-day plan'
    };
    const categories = ['rules', 'practice', 'packing', 'travel']
      .map((phase) => ({ phase, title: phaseLabels[phase], tasks: tasks.filter((task) => task.phase === phase) }))
      .filter((category) => category.tasks.length);
    const officialLinks = [];
    if (mode === 'flight') {
      officialLinks.push(
        { title: 'U.S. DOT: Flying with a pet', url: 'https://www.transportation.gov/airconsumer/flying_with_pet' },
        { title: 'IATA: Traveler\'s Pet Corner', url: 'https://www.iata.org/en/programs/cargo/live-animals/pets/' }
      );
    }
    if (destination === 'cross_border') {
      officialLinks.push({ title: 'USDA APHIS: Pet travel', url: 'https://www.aphis.usda.gov/pet-travel' });
      if (pet === 'dog') officialLinks.push({ title: 'CDC: Bringing a dog into the United States', url: 'https://www.cdc.gov/importation/dogs/index.html' });
    }

    const travelDate = tripDate || 'your travel day';
    let timing = tripDate ? `Travel date: ${tripDate}.` : 'Add a travel date when known so deadlines can be checked against the real itinerary.';
    if (tripDate && today) {
      const depart = new Date(`${tripDate}T00:00:00Z`);
      const current = new Date(`${today}T00:00:00Z`);
      const daysUntil = Math.round((depart.getTime() - current.getTime()) / 86400000);
      if (!Number.isNaN(daysUntil)) {
        timing = daysUntil < 0
          ? `The selected date has passed. Update it before using this plan.`
          : daysUntil === 0
            ? 'Travel is today. Leave any unresolved critical task open and change the trip if it cannot be completed safely.'
            : `${daysUntil} day${daysUntil === 1 ? '' : 's'} until travel on ${tripDate}.`;
      }
    }

    let title = `${modeLabel} checklist for ${travelDate}`;
    let copy = `Work through ${tasks.length} trip-specific tasks. Checked boxes are progress markers, not proof that a carrier, airline, property, or destination will accept the pet.`;
    if (health !== 'stable' || stress === 'severe') {
      title = 'Get veterinary guidance before travel';
      copy = 'A health change, activity restriction, or severe travel stress needs trip-specific veterinary guidance before gear and packing decisions are finalized.';
    } else if ((mode === 'flight' || destination === 'cross_border') && documents !== 'ready') {
      title = 'Finish rules and records before committing';
      copy = 'Flight and border requirements can change and may have firm timelines. Confirm the exact route and complete the missing record checks before relying on the trip plan.';
    } else if (mode === 'flight' && carrier === 'none') {
      title = 'Start carrier practice before the flight';
      copy = 'Carrier fit and calm familiarity need time. Confirm the exact airline and route rules, then introduce the correct carrier in small, positive steps.';
    }

    return {
      primary: modeLabel,
      unit: 'checklist',
      title,
      copy,
      basis: `${timing} This plan reflects the selected mode, duration, destination, weather, carrier familiarity, records, medication, stress, and health context.`,
      categories,
      tasks,
      officialLinks,
      note: 'Rules, routes, aircraft, border requirements, weather, and a pet\'s health can change. Recheck the decisive official source and your veterinarian close to departure.',
      details: {
        criticalIds: tasks.filter((task) => task.priority === 'critical').map((task) => task.id),
        destination,
        mode,
        modeLabel,
        pet,
        taskCount: tasks.length,
        tripDate
      }
    };
  };

  const catEnrichment = (input) => {
    const profile = input || {};
    const lifeStage = allowed(profile.lifeStage, ['kitten', 'adult', 'senior'], 'adult');
    const energy = allowed(profile.energy, ['calm', 'mixed', 'high'], 'mixed');
    const catCount = clamp(Number(profile.catCount) || 1, 1, 6);
    const home = allowed(profile.home, ['compact', 'average', 'multiroom'], 'average');
    const aloneTime = allowed(profile.aloneTime, ['short', 'workday', 'long'], 'workday');
    const windows = clamp(Number(profile.windows) || 3, 2, 4);
    const playStyle = allowed(profile.playStyle, ['ground', 'air', 'forage', 'climb', 'unsure'], 'unsure');
    const signal = allowed(profile.signal, ['balanced', 'scratch', 'night', 'food', 'low_interest', 'sudden_change'], 'balanced');
    const feeding = allowed(profile.feeding, ['meals', 'free', 'puzzle', 'mixed'], 'meals');
    const mobility = allowed(profile.mobility, ['full', 'low_jump', 'restricted'], 'full');
    const allowedResources = ['scratcher', 'hide', 'perch', 'food_puzzle', 'interactive_toy'];
    const resources = Array.isArray(profile.resources)
      ? [...new Set(profile.resources.filter((resource) => allowedResources.includes(resource)))]
      : [];
    const has = (resource) => resources.includes(resource);
    const multiCat = catCount > 1;
    const factors = [];

    const lifeStageLabel = lifeStage === 'kitten' ? 'young cat' : lifeStage === 'senior' ? 'senior cat' : 'adult cat';
    const energyLabel = energy === 'calm' ? 'calm energy' : energy === 'high' ? 'high energy' : 'mixed energy';
    const homeLabel = home === 'compact' ? 'compact home' : home === 'multiroom' ? 'multi-room home' : 'average-size home';
    const windowLabel = `${windows} predictable daily window${windows === 1 ? '' : 's'}`;
    factors.push(lifeStageLabel, energyLabel, homeLabel, windowLabel);
    if (multiCat) factors.push(`${catCount}-cat household needs separated choices`);
    if (aloneTime === 'long') factors.push('long stretches without a person at home');
    if (mobility !== 'full') factors.push(mobility === 'restricted' ? 'veterinary movement restrictions' : 'lower-jump preference');
    if (signal !== 'balanced') {
      const signalLabels = {
        scratch: 'furniture scratching is the current priority',
        night: 'late-night activity is the current priority',
        food: 'food focus is the current priority',
        low_interest: 'low toy interest is the current priority',
        sudden_change: 'a sudden behavior change needs attention'
      };
      factors.push(signalLabels[signal]);
    }

    const resourceDefinitions = [
      {
        id: 'hide',
        title: 'Safe retreat',
        ready: has('hide'),
        action: multiCat
          ? 'Offer more than one quiet retreat in different areas so a cat cannot block every option.'
          : 'Keep one quiet hiding place available without forcing interaction.',
        lowCost: 'Low-cost option: place a sturdy open cardboard box on its side in a quiet area.'
      },
      {
        id: 'scratcher',
        title: 'Scratching choice',
        ready: has('scratcher'),
        action: signal === 'scratch'
          ? 'Place a stable scratcher directly beside the furniture target and another near a favorite resting place. Reward use and protect the furniture while the new habit forms.'
          : 'Offer a stable scratching surface near a resting or high-use area and notice whether vertical, horizontal, or angled feels best.',
        lowCost: 'Low-cost option: use a wide corrugated cardboard scratch pad that stays still during a full-body stretch.'
      },
      {
        id: 'perch',
        title: mobility === 'full' ? 'Vertical choice' : 'Easy observation route',
        ready: has('perch'),
        action: mobility === 'full'
          ? 'Create a stable route to an elevated resting or window-viewing spot with an easy way down.'
          : 'Use low, stable steps and wide resting surfaces. Do not ask for jumps that look difficult or conflict with veterinary guidance.',
        lowCost: 'Low-cost option: arrange stable furniture as a gradual route to a cleared, secure viewing spot.'
      },
      {
        id: 'food_puzzle',
        title: 'Foraging opportunity',
        ready: has('food_puzzle'),
        action: feeding === 'free'
          ? 'Measure a portion of the normal daily food and use it for one easy search or puzzle instead of adding extra calories.'
          : 'Move a small measured portion of the normal meal into an easy puzzle or search and increase difficulty only when the cat stays relaxed.',
        lowCost: 'Low-cost option: hide a few pieces of the measured meal in an open egg carton or loosely folded paper parcel while supervising the first trial.'
      },
      {
        id: 'interactive_toy',
        title: 'Predatory play',
        ready: has('interactive_toy'),
        action: 'Offer a supervised chase-and-catch sequence that matches the cat\'s preferred movement. Let the cat catch the toy and end before frustration or exhaustion.',
        lowCost: 'Low-cost option: roll a paper ball or move a large paper strip along the floor while supervising, then store it out of reach.'
      }
    ];

    const preferredPlay = {
      ground: 'Move a wand or sturdy toy along the floor, pause behind cover, and allow a catch.',
      air: 'Use short, low aerial movements with a catchable toy. Avoid repeated high jumps when landings are uncertain.',
      forage: 'Use part of the regular meal for a simple search, then adjust difficulty from the cat\'s response.',
      climb: mobility === 'full'
        ? 'Create a short route between stable levels and finish at a safe viewing or resting point.'
        : 'Use a low, stable route with no difficult jumps and finish at an easy resting point.',
      unsure: 'Run a preference trial: ground movement, gentle air movement, food search, and a viewing perch on separate days. Keep the options the cat chooses.'
    }[playStyle];

    const morningText = mobility === 'restricted'
      ? 'Follow the veterinarian\'s movement limits. Use calm sniffing, an easy food search, or gentle interaction that the cat can leave at any time.'
      : preferredPlay;
    const awayText = aloneTime === 'short'
      ? 'Leave one safe independent option available and put string, feathers, cords, and small swallowable pieces away when unsupervised.'
      : aloneTime === 'long'
        ? 'Set up two quiet, low-risk stations in separate areas and rotate only one item at a time. Keep strings, feathers, cords, and small swallowable pieces for supervised use.'
        : 'Leave one quiet foraging, viewing, hiding, or sturdy solo-toy option. Keep strings, feathers, cords, and small swallowable pieces for supervised use.';
    const eveningText = signal === 'night'
      ? 'Use the most engaging supervised play window before the evening meal, allow a catch, then shift to food and a predictable quiet period. Track whether wake-ups change across the week.'
      : 'Use a supervised chase, catch, and food-linked finish before the household settles. Stop if the cat walks away, hides, pants, or becomes frustrated.';
    const socialText = multiCat
      ? 'Watch each cat separately. Spread food, water, litter, rest, scratch, hiding, and play choices so one cat cannot guard the only route or resource.'
      : 'Offer attention predictably, but let the cat choose whether to play, watch, hide, or rest.';

    const dailyAnchors = [
      {
        id: 'morning',
        time: 'Morning',
        title: 'Open with the preferred activity',
        text: morningText,
        lowCost: 'Low-cost option: use a paper ball, open box, or a few pieces of the measured meal in a simple search.'
      },
      {
        id: 'away',
        time: 'While away',
        title: 'Leave a safe independent choice',
        text: awayText,
        lowCost: 'Low-cost option: rotate a cardboard box, paper ball, or cleared window view instead of buying several new toys.'
      },
      {
        id: 'environment',
        time: 'All day',
        title: 'Keep essential choices available',
        text: socialText,
        lowCost: 'Low-cost option: separate existing bowls, boxes, resting blankets, and scratchers into different usable areas.'
      },
      {
        id: 'evening',
        time: 'Evening',
        title: signal === 'night' ? 'Build a play, eat, settle rhythm' : 'Close with play, food, and recovery',
        text: eveningText,
        lowCost: 'Low-cost option: reuse the morning toy with a different movement pattern, then offer part of the regular meal.'
      }
    ];

    const weekPlan = [
      {
        id: 'day-1',
        day: 'Day 1',
        title: 'Observe real preferences',
        text: 'Offer two different activities without pushing. Record which movement, texture, location, and time earned relaxed interest.',
        lowCost: 'Low-cost option: compare a paper ball, an open box, and a food search using items already at home.'
      },
      {
        id: 'day-2',
        day: 'Day 2',
        title: signal === 'scratch' ? 'Redirect the furniture target' : 'Test scratching preference',
        text: signal === 'scratch'
          ? 'Place a stable preferred scratcher beside the target, protect the surface, and reward the cat for choosing the scratcher. Do not punish scratching.'
          : 'Offer a stable scratcher near a resting or high-use area and notice whether the cat chooses vertical, horizontal, or angled use.',
        lowCost: 'Low-cost option: use a broad corrugated cardboard pad and keep it from sliding.'
      },
      {
        id: 'day-3',
        day: 'Day 3',
        title: mobility === 'full' ? 'Add height and hiding choice' : 'Add a low route and quiet retreat',
        text: mobility === 'full'
          ? 'Check that the cat can reach a secure viewing place, retreat into a quiet hide, and leave both without being cornered.'
          : 'Build a wide, stable low route to a resting or viewing place and keep an easy floor-level retreat available.',
        lowCost: 'Low-cost option: use stable furniture for steps and a sturdy open box for hiding.'
      },
      {
        id: 'day-4',
        day: 'Day 4',
        title: 'Turn part of a meal into a search',
        text: 'Use a measured portion of the normal food, begin with an easy find, and stop if the cat becomes frustrated or cannot access it comfortably.',
        lowCost: 'Low-cost option: use an open egg carton or several wide cups placed in view for the first trial.'
      },
      {
        id: 'day-5',
        day: 'Day 5',
        title: 'Practice chase and catch',
        text: preferredPlay,
        lowCost: 'Low-cost option: roll a paper ball or move a large paper strip while supervising, then put it away.'
      },
      {
        id: 'day-6',
        day: 'Day 6',
        title: 'Rotate one thing, not everything',
        text: 'Bring back a successful activity from earlier in the week and change only its location, movement, or timing so the routine stays familiar.',
        lowCost: 'Low-cost option: move an existing box, scratcher, or toy to a second safe location for the session.'
      },
      {
        id: 'day-7',
        day: 'Day 7',
        title: 'Keep what the cat chose',
        text: 'Review engagement, sleep, scratching, food focus, tension, and recovery. Keep the two strongest choices and change one weak point next week.',
        lowCost: 'Low-cost option: write the two winning activities on a note and repeat them at the same daily anchors.'
      }
    ];

    let primary = signal === 'balanced' ? 'Balanced routine' : 'Priority routine';
    let title = `${windowLabel} with a 7-day rotation`;
    let copy = `Build around choice, predictable interaction, safe retreat, separated resources, scratching, foraging, and play that matches this ${lifeStageLabel}.`;
    if (signal === 'scratch') {
      primary = 'Scratching support';
      title = 'Redirect scratching without punishment';
      copy = 'Start with location and surface preference, protect the furniture, reward the better choice, and keep the rest of the enrichment routine predictable.';
    } else if (signal === 'night') {
      primary = 'Evening rhythm';
      title = 'Test a calmer play, eat, settle routine';
      copy = 'Use a predictable evening sequence and track the result for seven days. Normal bursts of activity can still happen, so judge the trend instead of expecting an instant fix.';
    } else if (signal === 'food') {
      primary = 'Foraging routine';
      title = 'Give part of the regular meal a job';
      copy = 'Use measured food for easy searches or puzzles, keep feeding totals unchanged, and combine food work with hiding, scratching, viewing, and social choice.';
    } else if (signal === 'low_interest') {
      primary = 'Preference trial';
      title = 'Find what this cat actually chooses';
      copy = 'Compare movement, food, height, hiding, texture, and timing without forcing participation. Keep the options that produce relaxed, repeatable interest.';
    } else if (signal === 'sudden_change') {
      primary = 'Health first';
      title = 'Arrange a veterinary check for the sudden change';
      copy = 'Pain, illness, fear, or another stressor can change play, sleep, scratching, appetite, hiding, or social behavior. Keep the routine gentle while a veterinarian helps rule out a medical cause.';
    }

    const missingCount = resourceDefinitions.filter((resource) => !resource.ready).length;
    return {
      primary,
      unit: 'weekly focus',
      title,
      copy,
      basis: `This plan uses ${windowLabel}, ${catCount} cat${catCount === 1 ? '' : 's'}, ${homeLabel}, ${energyLabel}, the selected play and feeding preferences, mobility needs, time alone, and ${missingCount} resource gap${missingCount === 1 ? '' : 's'}.`,
      factors,
      resources: resourceDefinitions,
      dailyAnchors,
      weekPlan,
      note: signal === 'sudden_change'
        ? 'A sudden or persistent change in behavior, appetite, movement, sleep, litter use, grooming, vocalization, or social contact deserves veterinary guidance. This routine cannot identify the cause.'
        : 'Let the cat opt out. Stop if play causes fear, frustration, panting, pain, conflict, or repeated avoidance. A sudden or persistent behavior change deserves veterinary guidance.',
      details: {
        catCount,
        missingCount,
        signal,
        windows,
        resources
      }
    };
  };

  const groomingRoutine = (input) => {
    const profile = input || {};
    const species = allowed(profile.species, ['dog', 'cat'], 'dog');
    const lifeStage = allowed(profile.lifeStage, ['young', 'adult', 'senior'], 'adult');
    const coat = allowed(profile.coat, ['short', 'double', 'long', 'curly', 'hairless'], 'short');
    const coatState = allowed(profile.coatState, ['clear', 'shedding', 'tangles', 'mats'], 'clear');
    const lifestyle = allowed(profile.lifestyle, ['home', 'outdoor', 'swims'], 'home');
    const skin = allowed(profile.skin, ['normal', 'sensitive', 'active'], 'normal');
    const handling = allowed(profile.handling, ['comfortable', 'uneasy', 'distressed'], 'comfortable');
    const nails = allowed(profile.nails, ['comfortable', 'long', 'overgrown'], 'comfortable');
    const dental = allowed(profile.dental, ['daily', 'some', 'none', 'signs'], 'some');
    const ears = allowed(profile.ears, ['clear', 'risk', 'signs'], 'clear');
    const today = /^\d{4}-\d{2}-\d{2}$/.test(String(profile.today || ''))
      ? String(profile.today)
      : new Date().toISOString().slice(0, 10);
    const completed = profile.completed && typeof profile.completed === 'object' ? profile.completed : {};
    const validDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
    const addDays = (value, days) => {
      const date = new Date(`${value}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() + days);
      return date.toISOString().slice(0, 10);
    };
    const dayDifference = (from, to) => Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86400000);
    const formatDate = (value) => new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const factors = [];
    const alerts = [];

    const speciesLabel = species === 'cat' ? 'cat' : 'dog';
    const coatLabels = { short: 'short or smooth coat', double: 'double coat', long: 'long or silky coat', curly: 'curly or continuously growing coat', hairless: 'hairless or minimal coat' };
    const coatStateLabels = { clear: 'no current coat problem', shedding: 'heavy or seasonal shedding', tangles: 'tangles forming now', mats: 'mats present now' };
    const lifestyleLabels = { home: 'mostly home-based activity', outdoor: 'frequent outdoor dirt or debris', swims: 'regular swimming or water exposure' };
    const skinLabels = { normal: 'no current skin concern', sensitive: 'sensitive or easily dried skin', active: 'an active skin problem or treatment plan' };
    const handlingLabels = { comfortable: 'comfortable routine handling', uneasy: 'handling that needs gradual practice', distressed: 'intense grooming distress' };
    factors.push(speciesLabel, coatLabels[coat], `${lifeStage} life stage`);
    if (coatState !== 'clear') factors.push(coatState === 'shedding' ? 'heavy or seasonal shedding' : coatState === 'tangles' ? 'tangles forming now' : 'mats present now');
    if (lifestyle !== 'home') factors.push(lifestyle === 'swims' ? 'regular swimming or water exposure' : 'frequent outdoor dirt or debris');
    if (skin !== 'normal') factors.push(skin === 'sensitive' ? 'sensitive or easily dried skin' : 'active skin problem or treatment');
    if (handling !== 'comfortable') factors.push(handling === 'uneasy' ? 'handling needs gradual practice' : 'grooming causes intense distress');
    if (lifeStage === 'senior') factors.push('senior comfort and mobility need closer observation');

    if (coatState === 'mats') alerts.push('Tight mats can hide skin injury and may worsen when wet. Do not cut close mats with scissors or bathe over them. Ask a qualified groomer or veterinarian for safe removal.');
    if (skin === 'active') alerts.push('An active rash, sore, infection, parasite concern, or prescribed skin plan needs veterinary guidance before changing shampoos, bathing frequency, or clipping.');
    if (nails === 'overgrown') alerts.push('Overgrown, curled, split, bleeding, or painful nails may need staged trimming by a veterinarian or qualified groomer. Do not force a large correction at home.');
    if (dental === 'signs') alerts.push('Bad breath with red or bleeding gums, drooling, mouth pain, loose teeth, swelling, or difficulty eating needs a veterinary dental exam. Home brushing cannot treat disease below the gumline.');
    if (ears === 'signs') alerts.push('Redness, odor, discharge, swelling, pain, or repeated head shaking needs veterinary assessment. Do not add cleaner or insert cotton swabs into the ear canal.');
    if (handling === 'distressed') alerts.push('Stop forced grooming when the pet is panicking, trying to escape, freezing, growling, biting, or showing escalating fear. Use gradual cooperative-care practice and professional help.');

    let brushInterval = coat === 'short' ? 7 : coat === 'double' ? 3 : coat === 'hairless' ? 4 : 1;
    if (coatState === 'shedding') brushInterval = Math.min(brushInterval, 2);
    if (coatState === 'tangles') brushInterval = 1;
    const brushCadence = coat === 'hairless'
      ? 'Check skin and folds twice weekly; use an individualized skin-care method'
      : coatState === 'mats'
        ? 'Professional mat assessment before routine brushing or bathing'
        : coat === 'long' || coat === 'curly'
          ? 'Brief daily brush and comb-through'
          : coat === 'double'
            ? 'Every 2 to 3 days; check daily during a heavy shed'
            : coatState === 'shedding'
              ? 'Every 1 to 2 days during the heavy shed'
              : 'Weekly starting check; increase when loose hair or tangles appear';
    const brushAction = coatState === 'mats'
      ? 'Keep the area dry and avoid pulling, scissors, or force. Arrange safe removal, then restart short maintenance sessions before tangles tighten.'
      : coat === 'hairless'
        ? 'Look for oil buildup, dryness, redness, sores, or irritation in folds and contact areas. Follow product and bathing guidance for this individual pet.'
        : coat === 'double'
          ? 'Work in small sections through loose undercoat without scraping skin or shaving a healthy double coat for convenience.'
          : coat === 'long' || coat === 'curly'
            ? 'Brush to the skin in small sections, then use a comb to find hidden tangles around ears, armpits, legs, tail, and friction areas.'
            : 'Use a gentle brush or grooming mitt to remove loose coat while checking skin comfort, lumps, debris, and parasites.';

    let bathInterval = species === 'dog' && skin === 'normal' && lifestyle === 'home' ? 42 : null;
    let bathCadence = species === 'cat' ? 'As needed; most cats do not need calendar-based baths' : 'Reassess around every 4 to 6 weeks, then bathe for real coat or hygiene need';
    let bathAction = species === 'cat'
      ? 'Use brushing and spot cleaning first. Bathe only for a clear need, safe product guidance, or veterinary direction, and keep handling voluntary where possible.'
      : 'Brush out loose coat and tangles first, use lukewarm water and a pet-safe product, rinse completely, and dry the skin and coat without overheating.';
    if (lifestyle === 'swims') {
      bathCadence = 'Rinse and check after swimming; bathe when residue, dirt, or odor remains';
      bathAction = 'Rinse chlorine, salt, algae, sand, or other residue promptly, dry ears and skin folds gently, and use shampoo only when needed for the coat or veterinary plan.';
      bathInterval = null;
    } else if (lifestyle === 'outdoor') {
      bathCadence = 'Check after dirty outings; bathe when brushing or spot cleaning is not enough';
      bathInterval = null;
    }
    if (skin === 'sensitive') {
      bathCadence = lifestyle === 'swims'
        ? 'Rinse and check after swimming; use a gentle product only when a full bath is needed'
        : 'Need-based, with a gentle product and skin response check';
      if (lifestyle === 'swims') bathAction += ' Choose a gentle pet-safe product for full baths and watch the skin response after drying.';
      bathInterval = null;
    } else if (skin === 'active') {
      bathCadence = 'Veterinary treatment schedule only';
      bathAction = 'Use only the bathing, product, contact-time, drying, and follow-up plan given for the active skin problem.';
      bathInterval = null;
    }
    if (coatState === 'mats') {
      bathCadence = 'Wait until tight mats are assessed and safely removed';
      bathInterval = null;
    }

    const nailAction = nails === 'overgrown'
      ? 'Arrange a safe staged trim. Curled, split, bleeding, painful, or very overgrown nails need professional or veterinary help.'
      : nails === 'long'
        ? 'Check paw position and the quick, then make a small safe trim or book help. Stop for pain, bleeding, or uncertainty.'
        : 'Check every nail, dewclaws included, and trim only the safe tip when length begins to change stance, traction, or comfort.';
    const dentalAction = dental === 'signs'
      ? 'Book a veterinary dental exam. Do not brush a painful mouth or rely on a cosmetic, anesthesia-free cleaning to treat disease below the gumline.'
      : dental === 'daily'
        ? 'Keep the daily habit with pet-specific toothpaste and a soft brush. Reward cooperation and continue regular veterinary dental exams.'
        : dental === 'some'
          ? 'Build toward daily brushing with pet-specific toothpaste. Start with calm mouth handling and a few outer tooth surfaces, then increase gradually.'
          : 'Begin a positive introduction to pet-specific toothpaste and gentle mouth handling. Ask the veterinarian about effective alternatives if brushing is not yet possible.';
    const earAction = ears === 'signs'
      ? 'Arrange veterinary assessment before cleaning. Pain, odor, discharge, redness, swelling, or repeated shaking can need diagnosis and treatment.'
      : lifestyle === 'swims' || ears === 'risk'
        ? 'Look and smell after water exposure and at the weekly check. Dry the outer ear gently. Use cleaner only when a veterinarian has recommended the product and method.'
        : 'Look at the outer ear weekly for redness, odor, discharge, swelling, tenderness, or a new head-shaking pattern. Do not clean a normal ear just because a date arrived.';
    const pawAction = lifestyle === 'home'
      ? 'Check pads, nails, skin, and friction areas weekly for redness, cracks, debris, mats, swelling, licking, or tenderness.'
      : 'Check paws, pads, between-toe skin, coat, and contact areas after outdoor or water exposure. Remove safe surface debris and seek help for embedded, painful, or inflamed material.';

    const tasks = [
      {
        id: 'brush',
        title: coat === 'hairless' ? 'Skin and coat check' : 'Brush and comb',
        cadence: brushCadence,
        intervalDays: coatState === 'mats' ? null : brushInterval,
        statusOverride: coatState === 'mats' ? 'Get help first' : '',
        action: brushAction,
        stop: 'Stop for pain, skin pulling, panic, bleeding, or a hidden mat that does not separate with gentle finger work.'
      },
      {
        id: 'bath',
        title: 'Bath and dry',
        cadence: bathCadence,
        intervalDays: bathInterval,
        statusOverride: skin === 'active' ? 'Vet-led' : coatState === 'mats' ? 'Wait' : '',
        action: bathAction,
        stop: 'Stop for breathing trouble, weakness, overheating, intense panic, skin pain, or a product reaction. Rinse promptly and seek veterinary advice when needed.'
      },
      {
        id: 'nails',
        title: 'Nails and paw posture',
        cadence: nails === 'overgrown' ? 'Professional or veterinary assessment now' : nails === 'long' ? 'Check and address now, then review weekly' : 'Weekly length and comfort check; trim when needed',
        intervalDays: 7,
        statusOverride: nails === 'overgrown' ? 'Get help now' : nails === 'long' ? 'Check now' : '',
        action: nailAction,
        stop: 'Stop if the pet is escalating, the quick is unclear, the nail splits or bleeds, or the toe looks painful or swollen.'
      },
      {
        id: 'teeth',
        title: 'Teeth and gums',
        cadence: dental === 'signs' ? 'Veterinary dental exam before home brushing' : 'Daily home care is the goal',
        intervalDays: dental === 'signs' ? null : 1,
        statusOverride: dental === 'signs' ? 'Book exam' : '',
        action: dentalAction,
        stop: 'Stop and contact a veterinarian for bleeding, pain, swelling, loose teeth, drooling, mouth odor with inflammation, or difficulty eating.'
      },
      {
        id: 'ears',
        title: 'Ear check',
        cadence: ears === 'signs' ? 'Veterinary assessment before cleaning' : lifestyle === 'swims' || ears === 'risk' ? 'After water exposure and weekly' : 'Weekly look and smell check; clean only when needed',
        intervalDays: ears === 'signs' ? null : 7,
        statusOverride: ears === 'signs' ? 'Book exam' : '',
        action: earAction,
        stop: 'Stop for pain, redness, swelling, discharge, strong odor, repeated head shaking, balance change, or hearing concern.'
      },
      {
        id: 'paws-skin',
        title: 'Paws and skin scan',
        cadence: lifestyle === 'home' ? 'Weekly, plus any new licking or discomfort' : 'After outdoor or water exposure, plus a weekly full scan',
        intervalDays: lifestyle === 'home' ? 7 : 1,
        statusOverride: skin === 'active' ? 'Follow care plan' : '',
        action: pawAction,
        stop: 'Contact a veterinarian for a painful lump, open sore, spreading redness, swelling, discharge, parasite concern, persistent licking, or a change that does not settle.'
      },
      {
        id: 'professional',
        title: 'Professional grooming review',
        cadence: coatState === 'mats'
          ? 'Arrange safe mat assessment now'
          : handling === 'distressed'
            ? 'Plan a low-stress professional or veterinary strategy'
            : coat === 'long' || coat === 'curly'
              ? 'Discuss a recurring 4 to 8 week appointment based on coat length and home maintenance'
              : 'Use when coat, nails, handling, or hygiene exceeds safe home care',
        intervalDays: coatState === 'mats' || handling === 'distressed' ? null : coat === 'long' || coat === 'curly' ? 42 : null,
        statusOverride: coatState === 'mats' ? 'Arrange now' : handling === 'distressed' ? 'Plan support' : '',
        action: coatState === 'mats'
          ? 'Tell the groomer or veterinarian where the mats are, how the skin looks, and how the pet tolerates handling. Expect comfort and skin safety to take priority over coat length.'
          : handling === 'distressed'
            ? 'Ask for cooperative-care steps, shorter visits, quiet handling, and veterinary support when fear, pain, or medical needs make grooming unsafe.'
            : 'Use a professional when clipping, coat removal, nail care, skin-safe drying, or restraint is beyond what can be done calmly and safely at home.',
        stop: 'Do not continue a session that requires force, creates escalating fear, or risks cutting, burning, overheating, or injuring the pet.'
      }
    ];

    tasks.forEach((task) => {
      const lastCompleted = validDate(completed[task.id]) ? String(completed[task.id]) : '';
      let nextDate = '';
      let status = task.statusOverride || (task.intervalDays ? 'Add last date' : 'As needed');
      if (!task.statusOverride && lastCompleted && task.intervalDays) {
        nextDate = addDays(lastCompleted, task.intervalDays);
        const daysUntil = dayDifference(today, nextDate);
        status = daysUntil <= 0 ? 'Check now' : `Check ${formatDate(nextDate)}`;
      } else if (!task.statusOverride && lastCompleted && !task.intervalDays) {
        status = 'As needed';
      }
      task.lastCompleted = lastCompleted;
      task.nextDate = nextDate;
      task.status = status;
    });

    let primary = 'Maintenance plan';
    let title = 'A complete grooming routine you can track';
    let copy = 'Use the dates as review prompts, then let coat condition, skin, nail length, oral health, ears, lifestyle, comfort, and professional guidance decide what care is actually needed.';
    if (alerts.length) {
      primary = 'Address concerns first';
      title = 'Resolve the current concern before routine timing';
      copy = 'One or more answers point to pain, disease, tight mats, overgrown nails, ear or mouth signs, active skin care, or handling distress. Use the priority guidance before returning to routine maintenance.';
    } else if (coat === 'long' || coat === 'curly' || coatState === 'tangles') {
      primary = 'Coat-intensive plan';
      title = 'Prevent tangles before they become painful mats';
      copy = 'Frequent brief brush-and-comb sessions, calm handling, complete drying, and an appropriate professional schedule matter more than a single long grooming day.';
    } else if (handling === 'uneasy') {
      primary = 'Cooperative-care plan';
      title = 'Build comfort in smaller grooming steps';
      copy = 'Separate handling practice from full care, stop before fear escalates, and track small successful touches, tool introductions, or single-nail sessions.';
    }

    return {
      primary,
      unit: 'routine',
      title,
      copy,
      basis: `Built for the selected ${lifeStage} ${speciesLabel} profile with a ${coatLabels[coat]}, ${coatStateLabels[coatState]}, ${lifestyleLabels[lifestyle]}, ${skinLabels[skin]}, and ${handlingLabels[handling]}. Dates are check prompts, not proof that care is required.`,
      factors,
      alerts,
      tasks,
      note: 'Use pet-specific products and never use human toothpaste. Do not cut close mats with scissors, put cotton swabs into an ear canal, force a distressed pet, or continue through pain. Veterinary care takes priority when skin, mouth, ears, nails, behavior, or comfort changes.',
      details: {
        alertCount: alerts.length,
        completedCount: tasks.filter((task) => task.lastCompleted).length,
        species,
        today
      }
    };
  };

  const feedingTransition = (input) => {
    const profile = input || {};
    const species = allowed(profile.species, ['dog', 'cat'], 'dog');
    const sensitivity = allowed(profile.sensitivity, ['normal', 'sensitive', 'very'], 'normal');
    const reason = allowed(profile.reason, ['routine', 'life-stage', 'availability', 'vet'], 'routine');
    const symptoms = allowed(profile.symptoms, ['none', 'mild', 'concerning', 'urgent'], 'none');
    const presentation = allowed(profile.presentation, ['mixed', 'separate'], 'mixed');
    const amountMode = allowed(profile.amountMode, ['calorie', 'portion'], 'calorie');
    const oldUnit = allowed(profile.oldUnit, ['cup', 'can', 'pouch', 'gram', 'ounce'], 'cup');
    const newUnit = allowed(profile.newUnit, ['cup', 'can', 'pouch', 'gram', 'ounce'], 'cup');
    const requestedDays = allowed(Number(profile.days), [7, 10, 14, 21], 7);
    const minimumDays = sensitivity === 'very' ? 14 : sensitivity === 'sensitive' ? 10 : 7;
    const days = Math.max(requestedDays, minimumDays);
    const meals = clamp(Math.trunc(Number(profile.meals) || 2), 1, 6);
    const activeDay = clamp(Math.trunc(Number(profile.activeDay) || 1), 1, days);
    const holdCount = clamp(Math.trunc(Number(profile.holdCount) || 0), 0, 99);
    const finitePositive = (value, max) => {
      const number = Number(value);
      return Number.isFinite(number) && number > 0 && number <= max ? number : null;
    };
    const oldAmount = finitePositive(profile.oldAmount, 100000);
    const suppliedNewAmount = finitePositive(profile.newAmount, 100000);
    const oldDensity = finitePositive(profile.oldDensity, 20000);
    const newDensity = finitePositive(profile.newDensity, 20000);
    const unitLabels = {
      cup: ['cup', 'cups'],
      can: ['can', 'cans'],
      pouch: ['pouch', 'pouches'],
      gram: ['g', 'g'],
      ounce: ['oz', 'oz']
    };
    const amountDigits = (unit, amount) => unit === 'gram' && amount >= 10 ? 0 : unit === 'ounce' ? 1 : 2;
    const amountText = (amount, unit) => {
      const rounded = round(amount, amountDigits(unit, amount));
      const number = Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/0+$/, '').replace(/\.$/, '');
      const labels = unitLabels[unit];
      const label = rounded === 1 ? labels[0] : labels[1];
      return `${number} ${label}`;
    };
    const densityPerMeasure = (density, unit) => {
      if (unit === 'gram') return density / 1000;
      if (unit === 'ounce') return density * 28.349523125 / 1000;
      return density;
    };
    const densityText = (density, unit) => unit === 'gram' || unit === 'ounce'
      ? `${round(density)} kcal/kg`
      : `${round(density)} kcal/${unit}`;

    const invalidAmounts = oldAmount === null
      || (amountMode === 'calorie' && (oldDensity === null || newDensity === null))
      || (amountMode === 'portion' && suppliedNewAmount === null);
    if (invalidAmounts) {
      return {
        primary: 'Check the amounts',
        unit: 'before planning',
        title: 'Add valid food amounts before building the transition',
        copy: amountMode === 'calorie'
          ? 'Enter the current old-food amount plus both calorie-label values. All values must be greater than zero.'
          : 'Enter the full daily amount of each food. Both values must be greater than zero.',
        basis: 'No schedule is shown because missing or invalid amounts would create misleading bowl measurements.',
        factors: ['amounts need review'],
        alerts: ['Check the food label and the current feeding amount. Ask your veterinarian when the daily target is uncertain or medically directed.'],
        schedule: [],
        active: null,
        note: 'This planner organizes a gradual change. It does not calculate an individual calorie requirement or choose an appropriate diet.',
        details: { valid: false, days, meals, activeDay: 1, holdCount: 0, calorieMatched: false, targetCalories: null }
      };
    }

    const oldCaloriesPerMeasure = amountMode === 'calorie' ? densityPerMeasure(oldDensity, oldUnit) : null;
    const newCaloriesPerMeasure = amountMode === 'calorie' ? densityPerMeasure(newDensity, newUnit) : null;
    const targetCalories = amountMode === 'calorie' ? oldAmount * oldCaloriesPerMeasure : null;
    const newAmount = amountMode === 'calorie' ? targetCalories / newCaloriesPerMeasure : suppliedNewAmount;
    const stages = [10, 25, 50, 50, 75, 90, 100];
    const newPercentages = Array.from({ length: days }, (_, index) => {
      if (index === days - 1) return 100;
      const stageIndex = Math.floor((index * (stages.length - 1)) / Math.max(days - 1, 1));
      return stages[stageIndex];
    });
    const schedule = newPercentages.map((newPercent, index) => {
      const day = index + 1;
      const oldPercent = 100 - newPercent;
      const oldDaily = oldAmount * oldPercent / 100;
      const newDaily = newAmount * newPercent / 100;
      const repeatedRatio = index > 0 && newPercentages[index - 1] === newPercent;
      let check = 'Advance only if stool, appetite, energy, and comfort remain stable.';
      if (day === 1) check = 'Start at this ratio and record stool, appetite, energy, and comfort.';
      if (repeatedRatio) check = 'Keep this ratio for another planned day and observe before advancing.';
      if (day === days) check = 'Stay at the full new-food amount only if it remains well tolerated.';
      return {
        day,
        oldPercent,
        newPercent,
        oldDaily: round(oldDaily, 4),
        newDaily: round(newDaily, 4),
        oldPerMeal: round(oldDaily / meals, 4),
        newPerMeal: round(newDaily / meals, 4),
        oldDailyText: amountText(oldDaily, oldUnit),
        newDailyText: amountText(newDaily, newUnit),
        oldPerMealText: amountText(oldDaily / meals, oldUnit),
        newPerMealText: amountText(newDaily / meals, newUnit),
        totalCalories: targetCalories === null ? null : round((oldDaily * oldCaloriesPerMeasure) + (newDaily * newCaloriesPerMeasure), 1),
        check
      };
    });

    const factors = [species, `${days}-day schedule`, `${meals} meal${meals === 1 ? '' : 's'} per day`];
    if (sensitivity !== 'normal') factors.push(sensitivity === 'very' ? 'history of marked digestive sensitivity' : 'digestive sensitivity');
    if (reason === 'vet') factors.push('veterinarian-directed food change');
    if (presentation === 'separate') factors.push('foods offered side by side');
    factors.push(amountMode === 'calorie' ? 'calorie-label matching' : 'known daily portions');
    const alerts = [];
    if (symptoms === 'mild') alerts.push('Keep the last well-tolerated ratio instead of increasing the new food today. If the mild change does not settle, contact your veterinarian.');
    if (symptoms === 'concerning') alerts.push('Repeated vomiting, liquid diarrhea, pain, lethargy, dehydration concern, or refusing food needs veterinary guidance. Pause the planned increase.');
    if (symptoms === 'urgent') alerts.push('Blood, collapse, breathing trouble, severe weakness, repeated unproductive retching, a swollen abdomen, or a suspected toxin or foreign object needs urgent veterinary care now.');
    if (reason === 'vet') alerts.push('A prescription or therapeutic-diet transition should follow the veterinarian\'s exact timing, amount, and monitoring instructions even when they differ from this planner.');

    let primary = 'Gradual transition';
    let title = `${days}-day food transition with real bowl amounts`;
    let copy = 'Use each ratio as a checkpoint. Advance only after stool, appetite, energy, and comfort remain stable, and repeat a well-tolerated day when more time is needed.';
    let action = 'Use the active day below and record what you observe before moving forward.';
    if (reason === 'vet') {
      primary = 'Veterinarian-led plan';
      title = 'Use the prescribed transition first';
      copy = 'The schedule can organize measurements and notes, but it cannot replace instructions for a therapeutic diet, allergy trial, diabetes, kidney disease, pancreatitis, or another medical need.';
      action = 'Confirm the active ratio and daily amounts with the veterinary team before changing them.';
    }
    if (symptoms === 'mild') {
      primary = 'Hold this ratio';
      title = 'Give the last tolerated step more time';
      copy = 'A soft stool or mild gas with otherwise normal appetite, energy, and comfort is a reason to stop increasing the new food and observe, not a reason to rush the schedule.';
      action = 'Repeat the last well-tolerated ratio and contact your veterinarian if the change persists, worsens, or is joined by another symptom.';
    }
    if (symptoms === 'concerning') {
      primary = 'Pause and call';
      title = 'Get veterinary guidance before continuing';
      copy = 'The current signs are more important than the planned day. Stop increasing the new food and contact the veterinary team for next steps.';
      action = species === 'cat'
        ? 'Contact your veterinarian promptly, especially when your cat is refusing food or eating much less than usual.'
        : 'Contact your veterinarian and describe the vomiting, stool, appetite, energy, pain, and hydration changes.';
    }
    if (symptoms === 'urgent') {
      primary = 'Urgent veterinary care';
      title = 'Do not use a food schedule for these signs';
      copy = 'Seek urgent veterinary care now. A transition ratio cannot safely address a possible emergency.';
      action = 'Call a veterinarian or emergency clinic now and follow their feeding and transport instructions.';
    }

    const presentationNote = presentation === 'separate'
      ? species === 'cat'
        ? 'Offer the measured old and new foods in separate bowls so your cat can investigate the new smell and texture without losing access to the familiar food. Keep track of how much is actually eaten.'
        : 'Offer the measured foods separately only when that makes intake easier to observe. Prevent another pet from taking either portion.'
      : 'Mix the measured portions consistently so selective eating does not make the real ratio different from the plan.';
    const basis = amountMode === 'calorie'
      ? `${amountText(oldAmount, oldUnit)} of the old food at ${densityText(oldDensity, oldUnit)} supplies about ${round(targetCalories)} kcal per day. At 100% new food, ${amountText(newAmount, newUnit)} at ${densityText(newDensity, newUnit)} matches that calorie target before any veterinarian-directed adjustment.`
      : `The schedule scales from ${amountText(oldAmount, oldUnit)} of the old food to the supplied target of ${amountText(newAmount, newUnit)} of the new food. Calorie intake cannot be verified without both calorie-label values.`;

    return {
      primary,
      unit: 'transition',
      title,
      copy,
      basis,
      action,
      presentationNote,
      factors,
      alerts,
      schedule,
      active: schedule[activeDay - 1],
      note: 'This planner does not choose a diet or calculate an individual calorie need. Keep fresh water available. Continued vomiting, liquid diarrhea, pain, lethargy, dehydration concern, or refusing food needs veterinary guidance. Cats that stop eating need prompt attention.',
      details: {
        valid: true,
        days,
        requestedDays,
        meals,
        activeDay,
        holdCount,
        sensitivity,
        amountMode,
        calorieMatched: amountMode === 'calorie',
        targetCalories: targetCalories === null ? null : round(targetCalories, 1),
        oldFullAmount: round(oldAmount, 4),
        newFullAmount: round(newAmount, 4),
        oldFullText: amountText(oldAmount, oldUnit),
        newFullText: amountText(newAmount, newUnit),
        oldUnit,
        newUnit
      }
    };
  };

  const coolingHydration = (input) => {
    const profile = input || {};
    const species = allowed(profile.species, ['dog', 'cat'], 'dog');
    const temperatureUnit = allowed(profile.temperatureUnit, ['f', 'c'], 'f');
    const weightUnit = allowed(profile.weightUnit, ['lb', 'kg'], 'lb');
    const temperature = Number(profile.temperature);
    const weight = Number(profile.weight);
    if (!Number.isFinite(temperature) || !Number.isFinite(weight) || weight <= 0) return null;
    const humidity = allowed(profile.humidity, ['low', 'moderate', 'high'], 'moderate');
    const bodyRisk = allowed(profile.bodyRisk, ['standard', 'thick', 'flat', 'overweight'], 'standard');
    const lifeStage = allowed(profile.lifeStage, ['adult', 'young', 'senior'], 'adult');
    const health = allowed(profile.health, ['healthy', 'condition', 'restricted'], 'healthy');
    const activity = allowed(profile.activity, ['rest', 'light', 'moderate', 'intense'], 'light');
    const duration = allowed(profile.duration, ['brief', 'medium', 'long'], 'medium');
    const shade = allowed(profile.shade, ['full', 'partial', 'none'], 'partial');
    const water = allowed(profile.water, ['constant', 'planned', 'uncertain'], 'constant');
    const surface = allowed(profile.surface, ['cool', 'mixed', 'hot'], 'mixed');
    const acclimation = allowed(profile.acclimation, ['ready', 'limited', 'new'], 'limited');
    const diet = allowed(profile.diet, ['dry', 'mixed', 'wet'], 'mixed');
    const signs = allowed(profile.signs, ['none', 'early', 'emergency'], 'none');
    const tempC = temperatureUnit === 'c' ? temperature : (temperature - 32) * (5 / 9);
    const weightKg = weightUnit === 'kg' ? weight : weight / 2.20462;
    const waterMinMl = Math.max(10, Math.round((weightKg * 44) / 10) * 10);
    const waterMaxMl = Math.max(waterMinMl, Math.round((weightKg * 66) / 10) * 10);
    const waterMinOz = Math.round((waterMinMl / 29.5735) * 10) / 10;
    const waterMaxOz = Math.round((waterMaxMl / 29.5735) * 10) / 10;
    const factors = [];
    let caution = 0;
    const addFactor = (label, weightValue = 1) => {
      factors.push(label);
      caution += weightValue;
    };

    if (tempC >= 30) addFactor(`air temperature is ${Math.round(tempC)}°C (${Math.round((tempC * 9) / 5 + 32)}°F)`, 3);
    else if (tempC >= 26) addFactor(`air temperature is ${Math.round(tempC)}°C (${Math.round((tempC * 9) / 5 + 32)}°F)`, 2);
    else if (tempC >= 22) addFactor(`air temperature is ${Math.round(tempC)}°C (${Math.round((tempC * 9) / 5 + 32)}°F)`, 1);
    if (humidity === 'high') addFactor('high humidity can limit evaporative cooling', 2);
    else if (humidity === 'moderate' && tempC >= 26) addFactor('humidity adds to the warm conditions', 1);
    if (bodyRisk === 'thick') addFactor('a dense or double coat can hold heat', 1);
    if (bodyRisk === 'flat') addFactor('a flat face or short muzzle can reduce cooling ability', 2);
    if (bodyRisk === 'overweight') addFactor('extra body condition can increase heat strain', 1);
    if (lifeStage === 'young') addFactor('a puppy or kitten needs a more conservative plan', 1);
    if (lifeStage === 'senior') addFactor('a senior pet needs a more conservative plan', 1);
    if (health === 'condition') addFactor('a health condition may change heat and fluid tolerance', 2);
    if (health === 'restricted') addFactor('veterinary activity or fluid guidance controls the plan', 3);
    if (activity === 'moderate') addFactor('continuous moderate activity produces more body heat', 1);
    if (activity === 'intense') addFactor('intense activity produces substantial body heat', 3);
    if (duration === 'long') addFactor('a longer exposure gives heat more time to build', 1);
    if (shade === 'partial') addFactor('shade is only available for part of the outing', 1);
    if (shade === 'none') addFactor('no reliable shade or cool indoor recovery is available', 3);
    if (water === 'planned') addFactor('water is limited to planned stops', 1);
    if (water === 'uncertain') addFactor('fresh water access is uncertain', 3);
    if (surface === 'mixed') addFactor('some walking surfaces may hold heat', 1);
    if (surface === 'hot') addFactor('the planned surface may be hot enough to injure paws and add heat', 2);
    if (acclimation === 'limited') addFactor('the pet has limited recent warm-weather acclimation', 1);
    if (acclimation === 'new') addFactor('the weather is much warmer than the pet has recently experienced', 2);

    let band = 'low';
    if (tempC >= 24 || caution >= 3) band = 'moderate';
    if (tempC >= 30 || caution >= 7) band = 'high';
    if (signs === 'early') band = 'stop';
    if (signs === 'emergency') band = 'emergency';

    const bandContent = {
      low: {
        primary: 'Low caution',
        title: 'Use normal heat precautions and keep checking',
        copy: 'The selected conditions have fewer caution factors, but weather, surfaces, activity, and the pet can change during the outing.'
      },
      moderate: {
        primary: 'Moderate caution',
        title: 'Shorten the plan and watch recovery closely',
        copy: 'One or more conditions can make it harder to release heat. Choose an easier route, cooler timing, and a reliable exit to a cooler space.'
      },
      high: {
        primary: 'High caution',
        title: 'Choose a cooler plan',
        copy: 'The combined conditions call for a conservative change of plan. Move exercise indoors or wait for reliably cooler conditions instead of treating this band as permission to continue.'
      },
      stop: {
        primary: 'Stop and cool now',
        title: 'Stop activity and move to a cooler place',
        copy: 'A concerning heat sign was selected. End the activity, move to shade or a comfortably cool indoor space, offer access to water, and watch closely for rapid improvement or worsening.'
      },
      emergency: {
        primary: 'Emergency signs',
        title: 'Seek emergency veterinary care now',
        copy: 'A possible heatstroke sign was selected. Call the nearest veterinary hospital, begin cooling as they direct, and transport promptly. Do not wait for this planner or a product to solve it.'
      }
    }[band];
    const factorText = factors.length
      ? `Caution was raised by: ${factors.join('; ')}.`
      : 'No added caution factor was selected beyond the need for normal water, shade, surface, and recovery checks.';
    const dietText = {
      dry: 'Dry food contributes relatively little water, so most total water usually comes from drinking.',
      mixed: 'Both food moisture and drinking contribute to total water intake.',
      wet: 'Wet food can contribute substantial water, so drinking volume may be lower even when total intake is appropriate.'
    }[diet];
    const activityText = band === 'emergency'
      ? 'Stop the outing now. Follow the emergency veterinary and cooling instructions below; do not continue activity or use a cooling product to delay care.'
      : band === 'stop'
        ? 'Stop activity now and move to a reliably cooler place. Do not resume the outing while heat signs are present; seek veterinary guidance if recovery is not prompt or signs worsen.'
        : band === 'high'
          ? 'Replace the outdoor session with calm indoor movement or enrichment, or wait for a clearly cooler time. Do not use a cooling product to extend strenuous heat exposure.'
          : band === 'moderate'
            ? 'Choose the coolest practical time, reduce intensity and route length, and keep the pace voluntary. Turn back while the pet still looks comfortable.'
            : 'Keep the route flexible and the pace voluntary. Recheck weather, shade, surface, and recovery if conditions change.';
    const recoveryText = 'Do not wait for a fixed timer. Pause or end the outing at the first change in breathing, pace, gait, interest, coordination, gum appearance, or willingness to continue. Recovery should happen in reliable shade or a comfortably cool indoor space.';
    const surfaceText = surface === 'hot'
      ? 'Avoid the hot surface. Choose shaded grass or a cooler route, and do not rely on paw protection as permission to stay in unsafe heat.'
      : surface === 'mixed'
        ? 'Check every surface transition and choose shaded grass or cooler ground where possible. Pavement, sand, decks, and artificial turf can heat differently.'
        : 'A cooler surface helps, but it does not offset hot air, humidity, strenuous activity, or poor recovery access.';
    const waterText = health === 'restricted'
      ? `The general thermoneutral range would be about ${waterMinMl}-${waterMaxMl} mL (${waterMinOz}-${waterMaxOz} fl oz) of total water per day, but veterinary fluid guidance takes priority. ${dietText}`
      : `A rough thermoneutral starting range is ${waterMinMl}-${waterMaxMl} mL (${waterMinOz}-${waterMaxOz} fl oz) of total water per day. This includes water in food and is not a heat allowance or drinking target. ${dietText}`;
    const signalText = species === 'cat'
      ? 'Open-mouth breathing or panting in a cat, drooling, agitation, weakness, unusual hiding, vomiting, abnormal gums, poor coordination, or collapse needs prompt attention. Cats may hide illness, so do not wait for several signs.'
      : 'Harder panting than expected, excessive drooling, agitation, weakness, slowing, refusal to move, vomiting or diarrhea, abnormal gums, confusion, poor coordination, or collapse can signal heat illness.';
    const urgentText = 'Weakness or drowsiness, confusion, vomiting or diarrhea, abnormal dry or sticky gums, collapse, seizures, or severe breathing trouble can indicate heatstroke. Call an emergency veterinarian, move to shade or a comfortably cool indoor place, and begin cooling with room-temperature water and airflow while transporting as directed. Do not use an ice bath.';

    return {
      primary: bandContent.primary,
      unit: 'planning band',
      title: bandContent.title,
      copy: bandContent.copy,
      basis: `${factorText} This planning band does not measure body temperature or predict heatstroke.`,
      waterRange: `${waterMinMl}-${waterMaxMl} mL total water per day`,
      plan: [
        { icon: 'schedule', title: 'Timing and activity', text: activityText },
        { icon: 'water drop', title: 'Water baseline', text: waterText },
        { icon: 'pause circle', title: 'Breaks and recovery', text: recoveryText },
        { icon: 'landscape', title: 'Shade and surfaces', text: surfaceText },
        { icon: 'visibility', title: 'Watch the pet, not the clock', text: signalText },
        { icon: 'emergency', title: 'Emergency heat response', text: urgentText }
      ],
      note: 'Keep fresh water available and never leave a pet in a parked vehicle. A new thirst pattern, reduced urination, vomiting, diarrhea, weakness, or a medical condition needs veterinary guidance instead of a calculated water target.',
      details: {
        band,
        cautionFactors: factors,
        species,
        signs,
        tempC: Math.round(tempC * 10) / 10,
        waterMaxMl,
        waterMinMl,
        weightKg: Math.round(weightKg * 100) / 100
      }
    };
  };

  const captureForm = (form) => {
    const data = {};
    form.querySelectorAll('[data-field]').forEach((input) => {
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      data[input.dataset.field] = input.value;
    });
    return data;
  };

  const restoreForm = (form, data) => {
    if (!data || typeof data !== 'object') return false;
    Object.entries(data).forEach(([field, value]) => {
      form.querySelectorAll(`[data-field="${field}"]`).forEach((input) => {
        if (input.type === 'radio' || input.type === 'checkbox') {
          input.checked = input.value === String(value);
        } else {
          input.value = value;
        }
      });
    });
    return true;
  };

  const copyText = async (text) => {
    if (root.navigator && root.navigator.clipboard && typeof root.navigator.clipboard.writeText === 'function') {
      await root.navigator.clipboard.writeText(text);
      return true;
    }
    if (!root.document || typeof root.document.execCommand !== 'function') return false;
    const area = root.document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    root.document.body.appendChild(area);
    area.select();
    const copied = root.document.execCommand('copy');
    area.remove();
    return copied;
  };

  const track = (tool, action) => {
    const detail = { tool, action };
    if (Array.isArray(root.dataLayer)) {
      root.dataLayer.push({ event: 'pet_tool_action', pet_tool: tool, pet_tool_action: action });
    }
    if (typeof root.CustomEvent === 'function' && typeof root.dispatchEvent === 'function') {
      root.dispatchEvent(new root.CustomEvent('vep:pet-tool-action', { detail }));
    }
  };

  root.VepPetTools = Object.freeze({
    catExercise,
    catEnrichment,
    captureForm,
    clamp,
    copyText,
    coolingHydration,
    dogFood,
    dogExercise,
    feedingTransition,
    groomingRoutine,
    restoreForm,
    seniorMobility,
    sizeFit,
    travelReadiness,
    track
  });

  if (typeof root.CustomEvent === 'function' && typeof root.dispatchEvent === 'function') {
    root.dispatchEvent(new root.CustomEvent('vep:pet-tools-ready'));
  }
})(typeof window !== 'undefined' ? window : globalThis);
